<template>
  <div class="linha-assistente-dialog-root">
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    fullscreen
    persistent
    transition="dialog-bottom-transition"
  >
    <v-card v-if="value" class="linha-assistente-wizard-card d-flex flex-column rounded-0">
      <v-toolbar dark color="primary" flat class="linha-assistente-wizard-toolbar">
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>{{ $t('crudLinhas.wizard.pageTitle') }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn v-show="wizardStep >= 1" dark text :loading="savingLine" :disabled="savingLine" @click="save">
            {{ $t('crudLinhas.messages.save') }}
          </v-btn>
        </v-toolbar-items>
        <template v-slot:extension>
          <div class="d-flex align-center" style="width: 100%; flex-wrap: wrap;">
            <v-btn text dark small :disabled="wizardStep === 0" @click="wizardBack">
              <v-icon left>mdi-arrow-left</v-icon>
              {{ $t('crudLinhas.wizard.back') }}
            </v-btn>
            <v-spacer></v-spacer>
            <div class="d-flex flex-column align-center mx-2">
              <span class="caption white--text">{{ $t('crudLinhas.wizard.progressLabel', { current: wizardStep + 1, total: 3 }) }}</span>
              <span class="body-2 white--text font-weight-medium">{{ wizardStepTitle }}</span>
            </div>
            <v-spacer></v-spacer>
            <v-btn text dark small :disabled="wizardStep >= 2 || !canGoNext" @click="wizardNext">
              {{ $t('crudLinhas.wizard.next') }}
              <v-icon right>mdi-arrow-right</v-icon>
            </v-btn>
          </div>
        </template>
      </v-toolbar>

      <v-row no-gutters class="linha-assistente-wizard-main-row flex-grow-1">
        <v-col
          cols="12"
          :md="kmlGeometryPreview.open ? 7 : 12"
          :lg="kmlGeometryPreview.open ? 8 : 12"
          class="linha-assistente-wizard-col-content d-flex flex-column fill-height"
        >
      <v-card-text class="pa-2 pa-md-4 linha-assistente-wizard-scroll flex-grow-1" :style="assistantMainScrollStyle">
        <v-alert v-if="!canUseAssistant" type="warning" outlined class="mb-4">
          {{ $t('crudLinhas.wizard.noPermission') }}
        </v-alert>

        <template v-else>
          <!-- Step 0: KML -->
          <v-container v-show="wizardStep === 0">
            <v-alert type="info" dense outlined class="mb-4">
              {{ $t('crudLinhas.kmlAssistant.eachRowDirectionType') }}
            </v-alert>
            <v-row v-if="kmlRouteRows.length || kmlFenceRows.length" align="center" class="mb-2">
              <v-col cols="12" :md="kmlMapPreviewOpen ? 12 : 6">
                <v-switch
                  v-model="kmlMultiLineMode"
                  hide-details
                  class="mt-0"
                  :label="$t('crudLinhas.kmlAssistant.multipleLinesInKml')"
                />
              </v-col>
              <v-col v-if="kmlMultiLineMode" cols="12" :md="kmlMapPreviewOpen ? 4 : 6" class="d-flex align-center flex-wrap">
                <v-btn small outlined color="primary" class="mr-2 mb-2" @click="addKmlLineGroup">
                  <v-icon left small>mdi-plus</v-icon>
                  {{ $t('crudLinhas.kmlAssistant.addLineGroup') }}
                </v-btn>
              </v-col>
              <v-col v-if="kmlMultiLineMode" cols="12">
                <span class="text-caption">{{ $t('crudLinhas.kmlAssistant.multipleLinesHint') }}</span>
                <span class="text-caption d-block mt-1">{{ $t('crudLinhas.kmlAssistant.targetLineGroupsHint') }}</span>
              </v-col>
            </v-row>
            <v-form ref="formKml" v-model="formValidadoKml">
              <v-row align="center">
                <v-col cols="12" :md="kmlMapPreviewOpen ? 4 : 8">
                  <v-file-input
                    v-model="kmlFile"
                    accept=".kml,application/vnd.google-earth.kml+xml,application/xml,text/xml"
                    :label="$t('crudLinhas.kmlAssistant.selectKml')"
                    prepend-icon="mdi-file-xml-box"
                  />
                </v-col>
                <v-col cols="12" :md="kmlMapPreviewOpen ? 4 : 2">
                  <v-btn color="primary" :loading="parsingKml" :disabled="!kmlFile" @click="parseKmlFile">
                    {{ $t('button.process') }}
                  </v-btn>
                </v-col>
                <v-col cols="12" :md="kmlMapPreviewOpen ? 4 : 2">
                  <v-text-field
                    v-model.number="defaultOrdemStart"
                    type="number"
                    min="1"
                    hide-details
                    :label="$t('crudLinhas.kmlAssistant.firstFenceOrder')"
                  />
                </v-col>
                <v-col cols="12" :sm="6" :md="kmlMapPreviewOpen ? 6 : 3">
                  <v-select
                    v-model="defaultRouteTipo"
                    :items="tiposRota"
                    item-text="descricao_tipo_rota"
                    item-value="id_tipo_rota"
                    dense
                    hide-details
                    outlined
                    clearable
                    :disabled="!tiposRota.length"
                    :label="$t('crudLinhas.kmlAssistant.defaultRouteType')"
                    @change="applyDefaultRouteTipoToAll"
                  />
                </v-col>
                <v-col cols="12" :sm="6" :md="kmlMapPreviewOpen ? 6 : 3">
                  <v-select
                    v-model="defaultRouteSentido"
                    :items="sentidos"
                    item-text="descricao_sentido"
                    item-value="id_sentido"
                    dense
                    hide-details
                    outlined
                    clearable
                    :disabled="!sentidos.length"
                    :label="$t('crudLinhas.kmlAssistant.defaultRouteDirection')"
                    @change="applyDefaultRouteSentidoToAll"
                  />
                </v-col>
              </v-row>
              <v-alert
                v-if="kmlRouteCatalogsUnavailable"
                type="warning"
                dense
                outlined
                class="mt-2 mb-0"
              >
                {{ $t('crudLinhas.kmlAssistant.routeCatalogsUnavailable') }}
              </v-alert>
              <v-alert
                v-if="kmlEmpresasUnavailable"
                type="warning"
                dense
                outlined
                class="mt-2 mb-0"
              >
                {{ $t('crudLinhas.kmlAssistant.empresasUnavailable') }}
              </v-alert>
            </v-form>

            <h3 class="subtitle-1 mt-4">{{ $t('crudLinhas.kmlAssistant.routesFromKml') }}</h3>
            <v-list v-if="kmlRouteRows.length" class="elevation-1 mb-6 pa-0 rounded kml-routes-list" dense>
              <div v-for="(row, idx) in kmlRouteRowsVisible" :key="'rt-' + idx">
                <v-divider v-if="idx > 0" />
                <div :id="'kml-route-entry-' + idx" class="pa-4 kml-route-entry">
                  <v-row dense class="mb-2 flex-wrap align-start" style="width: 100%;">
                    <v-col cols="12" :md="kmlMapPreviewOpen ? 7 : kmlMultiLineMode ? 6 : 10" class="py-1" style="min-width: 0;">
                      <div class="caption grey--text text-uppercase mb-1">{{ $t('crudLinhas.kmlAssistant.kmlName') }}</div>
                      <div class="body-2 font-weight-medium text-wrap">{{ row.kmlLabel }}</div>
                      <div v-if="row.matchedExistingId" class="d-flex flex-wrap align-center mt-2">
                        <v-chip x-small color="success" outlined class="mr-1 mb-1">
                          {{
                            row.matchedFromCatalogPick
                              ? $t('crudLinhas.kmlAssistant.matchedExistingRotaManual')
                              : $t('crudLinhas.kmlAssistant.matchedExistingRota')
                          }}
                        </v-chip>
                        <v-btn x-small text class="mb-1 pa-0" @click="unlinkKmlRouteMatch(idx)">
                          {{ $t('crudLinhas.kmlAssistant.unlinkExistingRota') }}
                        </v-btn>
                      </div>
                    </v-col>
                    <v-col :cols="'auto'" class="py-1 d-flex flex-shrink-0">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            small
                            color="primary"
                            v-bind="attrs"
                            v-on="on"
                            :disabled="!kmlRouteRowHasMapGeometry(row)"
                            :aria-label="$t('crudLinhas.kmlAssistant.previewOnMap')"
                            @click="openKmlRouteMapPreview(idx)"
                          >
                            <v-icon small>mdi-map-search-outline</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t('crudLinhas.kmlAssistant.previewOnMap') }}</span>
                      </v-tooltip>
                    </v-col>
                    <v-col v-if="kmlMultiLineMode" cols="12" :md="kmlMapPreviewOpen ? 4 : 5" class="py-1" style="min-width: 0;">
                      <v-select
                        v-model="row.kmlGroupIds"
                        :items="kmlLineGroups"
                        item-text="label"
                        item-value="id"
                        multiple
                        chips
                        small-chips
                        deletable-chips
                        dense
                        hide-details
                        outlined
                        :label="$t('crudLinhas.kmlAssistant.targetLineGroups')"
                      />
                    </v-col>
                  </v-row>
                  <v-row dense class="flex-wrap" style="width: 100%;">
                    <v-col cols="12" :sm="kmlMapPreviewOpen ? 12 : 6" :md="kmlMapPreviewOpen ? 4 : 3" class="py-1" style="min-width: 0;">
                      <v-text-field
                        v-if="!row.matchedExistingId"
                        v-model="row.nome_rota"
                        dense
                        hide-details
                        outlined
                        :label="$t('routs.name')"
                        :rules="[r => !!r || $t('crudLinhas.validation.required')]"
                      />
                      <v-text-field
                        v-else
                        :value="kmlRouteNomeFieldDisplay(row)"
                        dense
                        hide-details
                        outlined
                        :label="$t('routs.name')"
                        disabled
                      />
                    </v-col>
                    <v-col cols="12" :sm="kmlMapPreviewOpen ? 12 : 6" :md="kmlMapPreviewOpen ? 4 : 2" class="py-1" style="min-width: 0;">
                      <v-text-field
                        v-if="!row.matchedExistingId"
                        v-model="row.cod_rota"
                        dense
                        hide-details
                        outlined
                        :label="$t('routs.code')"
                        :rules="[r => !!r || $t('crudLinhas.validation.required')]"
                      />
                      <v-text-field
                        v-else
                        :value="kmlRouteCodFieldDisplay(row)"
                        dense
                        hide-details
                        outlined
                        :label="$t('routs.code')"
                        disabled
                      />
                    </v-col>
                    <v-col cols="12" :sm="kmlMapPreviewOpen ? 12 : 6" :md="kmlMapPreviewOpen ? 4 : 3" class="py-1" style="min-width: 0;">
                      <v-select
                        v-model="row.id_empresa"
                        :items="empresas"
                        item-text="razao_social"
                        item-value="id_empresa"
                        dense
                        hide-details
                        outlined
                        :disabled="!!row.matchedExistingId"
                        :label="$t('crudLinhas.form.fields.selectCompany')"
                        :rules="[v => !!v || $t('crudLinhas.validation.companyRequired')]"
                      />
                    </v-col>
                    <v-col cols="12" :sm="kmlMapPreviewOpen ? 12 : 6" :md="kmlMapPreviewOpen ? 4 : 2" class="py-1" style="min-width: 0;">
                      <v-select
                        v-model="row.id_tipo_rota"
                        :items="tiposRota"
                        item-text="descricao_tipo_rota"
                        item-value="id_tipo_rota"
                        dense
                        hide-details
                        outlined
                        clearable
                        :label="$t('crudLinhas.form.routes.routeType')"
                        :rules="[v => v != null || $t('crudLinhas.validation.routeTypeRequired')]"
                      />
                    </v-col>
                    <v-col cols="12" :sm="kmlMapPreviewOpen ? 12 : 6" :md="kmlMapPreviewOpen ? 4 : 2" class="py-1" style="min-width: 0;">
                      <v-select
                        v-model="row.id_sentido"
                        :items="sentidos"
                        item-text="descricao_sentido"
                        item-value="id_sentido"
                        dense
                        hide-details
                        outlined
                        clearable
                        :label="$t('crudLinhas.form.routes.direction')"
                        :rules="[v => v != null || $t('crudLinhas.validation.required')]"
                      />
                    </v-col>
                  </v-row>

                </div>
              </div>
            </v-list>
            <div v-if="kmlRouteRows.length > kmlRouteListLimit" class="d-flex justify-center mb-4">
              <v-btn small outlined color="primary" @click="revealMoreKmlRoutes">
                {{ $t('crudLinhas.kmlAssistant.showMoreRoutes', { shown: kmlRouteListLimit, total: kmlRouteRows.length }) }}
              </v-btn>
            </div>
            <v-alert v-if="!kmlRouteRows.length" type="info" dense outlined>{{ $t('crudLinhas.kmlAssistant.noRoutesYet') }}</v-alert>

            <h3 class="subtitle-1 mt-4 d-flex align-center flex-wrap">
              <span>{{ $t('crudLinhas.kmlAssistant.fencesFromKml') }}</span>
              <v-btn small outlined color="primary" class="ml-3 mt-1 mb-1" @click="addKmlFenceRowFromCatalog" v-if="kmlRouteRows.length">
                <v-icon left small>mdi-database-plus</v-icon>
                {{ $t('crudLinhas.kmlAssistant.addFenceFromCatalog') }}
              </v-btn>
              <v-btn
                v-if="kmlIdentifiedFenceCount"
                small
                outlined
                color="error"
                class="ml-3 mt-1 mb-1"
                @click="removeAllKmlIdentifiedFences"
              >
                <v-icon left small>mdi-delete-sweep</v-icon>
                {{ $t('crudLinhas.kmlAssistant.removeAllKmlFences') }}
              </v-btn>
            </h3>
            <v-row v-if="kmlFenceRows.length" dense align="center" class="mb-2" style="margin-top:5px;">
              <v-col cols="12" class="py-1">
                <span class="caption grey--text">{{ $t('crudLinhas.kmlAssistant.applyDefaultsToSelectedHint') }}</span>
              </v-col>
              <v-col cols="auto" class="py-1 pr-1">
                <v-checkbox
                  :input-value="allKmlFencesSelected"
                  :indeterminate="someKmlFencesSelected && !allKmlFencesSelected"
                  hide-details
                  dense
                  class="mt-0 pt-0 mr-2"
                  @change="toggleSelectAllKmlFences"
                />
              </v-col>
              <v-col cols="12" :sm="kmlMapPreviewOpen ? 12 : 6" :md="kmlMapPreviewOpen ? 6 : 3" class="py-1">
                <v-select
                  v-model="defaultFenceTipo"
                  :items="tiposCerca"
                  item-text="descricao_tipo_cerca"
                  item-value="id_tipo_cerca"
                  dense
                  hide-details
                  outlined
                  clearable
                  :label="$t('crudLinhas.kmlAssistant.defaultFenceType')"
                  @change="applyDefaultFenceTipoToSelected"
                />
              </v-col>
              <v-col cols="12" :sm="kmlMapPreviewOpen ? 12 : 6" :md="kmlMapPreviewOpen ? 6 : 3" class="py-1">
                <v-select
                  v-model="defaultFenceSentido"
                  :items="sentidos"
                  item-text="descricao_sentido"
                  item-value="id_sentido"
                  dense
                  hide-details
                  outlined
                  clearable
                  :label="$t('crudLinhas.kmlAssistant.defaultFenceDirection')"
                  @change="applyDefaultFenceSentidoToSelected"
                />
              </v-col>
            </v-row>
            <v-list v-if="kmlFenceRows.length" class="elevation-1 pa-0 rounded kml-fences-list mb-6" dense>
              <div v-for="(row, idx) in kmlFenceRowsVisible" :key="'fn-' + idx">
                <v-divider v-if="idx > 0" />
                <div
                  :id="'kml-fence-entry-' + idx"
                  class="pa-4 kml-fence-entry"
                  :class="{ 'kml-fence-entry--selected': row.selected }"
                >
                  <v-row dense class="mb-2 flex-wrap align-start" style="width: 100%;">
                    <v-col cols="auto" class="py-1 pr-1">
                      <v-checkbox
                        :input-value="!!row.selected"
                        hide-details
                        dense
                        class="mt-0 pt-0"
                        :aria-label="$t('crudLinhas.kmlAssistant.selectFence')"
                        @change="onKmlFenceSelectedChange(idx, $event)"
                      />
                    </v-col>
                    <v-col cols="12" :md="kmlMapPreviewOpen ? 6 : kmlMultiLineMode ? 8 : 9" class="py-1" style="min-width: 0;">
                      <div class="caption grey--text text-uppercase mb-1">{{ $t('crudLinhas.kmlAssistant.kmlName') }}</div>
                      <div class="body-2 font-weight-medium text-wrap">
                        {{ row.kmlLabel
                        }}<template v-if="row.kind === 'point'"> (Point)</template
                        ><template v-else-if="row.kind === 'polygon'"> (Polygon)</template>
                      </div>
                      <div v-if="row.matchedExistingId" class="d-flex flex-wrap align-center mt-2">
                        <v-chip x-small color="success" outlined class="mr-1 mb-1">
                          {{
                            row.matchedFromCatalogPick
                              ? $t('crudLinhas.kmlAssistant.matchedExistingCercaManual')
                              : $t('crudLinhas.kmlAssistant.matchedExistingCerca')
                          }}
                        </v-chip>
                        <v-btn x-small text class="mb-1 pa-0" @click="unlinkKmlFenceMatch(idx)">
                          {{ $t('crudLinhas.kmlAssistant.unlinkExistingCerca') }}
                        </v-btn>
                      </div>
                    </v-col>
                    <v-col
                      :cols="kmlMapPreviewOpen ? 6 : 'auto'"
                      class="py-1 d-flex flex-column"
                      style="min-width: 0;"
                    >
                      <div class="d-flex justify-end align-center flex-shrink-0 mb-1">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              icon
                              small
                              color="primary"
                              class="mr-1"
                              v-bind="attrs"
                              v-on="on"
                              :disabled="!kmlFenceRowHasMapGeometry(row)"
                              :aria-label="$t('crudLinhas.kmlAssistant.previewOnMap')"
                              @click="openKmlFenceMapPreview(idx)"
                            >
                              <v-icon small>mdi-map-search-outline</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ $t('crudLinhas.kmlAssistant.previewOnMap') }}</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              icon
                              small
                              color="grey darken-1"
                              v-bind="attrs"
                              v-on="on"
                              :aria-label="$t('crudLinhas.kmlAssistant.removeStop')"
                              @click="removeKmlFenceRow(idx)"
                            >
                              <v-icon small>mdi-delete-outline</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ $t('crudLinhas.kmlAssistant.removeStop') }}</span>
                        </v-tooltip>
                      </div>
                      <v-select
                      v-if="kmlMultiLineMode"
                        v-model="row.kmlGroupIds"
                        :items="kmlLineGroups"
                        item-text="label"
                        item-value="id"
                        multiple
                        chips
                        small-chips
                        deletable-chips
                        dense
                        hide-details
                        outlined
                        :label="$t('crudLinhas.kmlAssistant.targetLineGroups')"
                      />
                    </v-col>
                  </v-row>
                  <v-row dense class="mb-2 flex-wrap" style="width: 100%;">
                    <v-col cols="12" class="py-1" style="min-width: 0;">
                      <template v-if="!row.fromKml">
                        <v-autocomplete
                          :value="normalizeCercaId(row.matchedExistingId)"
                          :items="cercasCatalog"
                          item-value="id_cerca"
                          item-text="nome_cerca"
                          clearable
                          dense
                          hide-details
                          outlined
                          attach
                          :menu-props="{ offsetY: true, maxHeight: 360 }"
                          :label="$t('crudLinhas.kmlAssistant.selectExistingCercaColumn')"
                          @input="onKmlFenceExistingCercaChange(idx, $event)"
                        >
                          <template v-slot:item="{ item }">
                            <v-list-item-content>
                              <v-list-item-title>{{ item.nome_cerca }}</v-list-item-title>
                              <v-list-item-subtitle v-if="item.cod_cerca != null && item.cod_cerca !== ''">
                                {{ item.cod_cerca }}
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </template>
                          <template v-slot:selection="{ item }">
                            <span v-if="item">
                              {{ item.cod_cerca != null && item.cod_cerca !== '' ? item.cod_cerca + ' — ' : '' }}{{ item.nome_cerca }}
                            </span>
                            <span v-else-if="row.matchedExistingId != null && kmlFenceExistingCodNomeLine(row)">
                              {{ kmlFenceExistingCodNomeLine(row) }}
                            </span>
                          </template>
                        </v-autocomplete>
                      </template>
                      <v-tooltip v-else bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <div
                            v-bind="attrs"
                            v-on="on"
                            class="pa-2"
                            style="min-height: 56px; border: 1px solid rgba(0, 0, 0, 0.38); border-radius: 4px;"
                          >
                            <div class="caption grey--text">{{ $t('crudLinhas.kmlAssistant.selectExistingCercaColumn') }}</div>
                            <span v-if="row.matchedExistingId != null && kmlFenceExistingCodNomeLine(row)" class="body-2 text--primary">
                              {{ kmlFenceExistingCodNomeLine(row) }}
                            </span>
                            <span v-else class="body-2 grey--text">—</span>
                          </div>
                        </template>
                        <span>{{ $t('crudLinhas.kmlAssistant.existingCercaDisabledForKmlStop') }}</span>
                      </v-tooltip>
                    </v-col>
                  </v-row>
                  <v-row dense class="flex-wrap" style="width: 100%;">
                    <v-col cols="12" :sm="kmlMapPreviewOpen ? 12 : 6" :md="kmlMapPreviewOpen ? 4 : 2" class="py-1" style="min-width: 0;">
                      <v-text-field
                        v-if="!row.matchedExistingId"
                        v-model="row.cod_cerca"
                        dense
                        hide-details
                        outlined
                        :label="$t('geofence.code')"
                      />
                      <v-text-field
                        v-else
                        :value="kmlFenceCodFieldDisplay(row)"
                        dense
                        hide-details
                        outlined
                        :label="$t('geofence.code')"
                        disabled
                      />
                    </v-col>
                    <v-col cols="12" :sm="kmlMapPreviewOpen ? 12 : 6" :md="kmlMapPreviewOpen ? 4 : 2" class="py-1" style="min-width: 0;">
                      <v-text-field
                        v-if="!row.matchedExistingId"
                        v-model="row.nome_cerca"
                        dense
                        hide-details
                        outlined
                        :label="$t('geofence.name')"
                      />
                      <v-text-field
                        v-else
                        :value="kmlFenceNomeFieldDisplay(row)"
                        dense
                        hide-details
                        outlined
                        :label="$t('geofence.name')"
                        disabled
                      />
                    </v-col>
                    <v-col cols="12" :sm="kmlMapPreviewOpen ? 12 : 6" :md="kmlMapPreviewOpen ? 4 : 2" class="py-1" style="min-width: 0;">
                      <v-select
                        v-model="row.id_tipo_cerca"
                        :items="tiposCerca"
                        item-text="descricao_tipo_cerca"
                        item-value="id_tipo_cerca"
                        dense
                        hide-details
                        outlined
                        clearable
                        :label="$t('crudLinhas.kmlAssistant.fenceFormat')"
                        :rules="[v => v != null || $t('crudLinhas.validation.fenceTypeRequired')]"
                      />
                    </v-col>
                    <v-col cols="12" :sm="kmlMapPreviewOpen ? 12 : 6" :md="kmlMapPreviewOpen ? 4 : 2" class="py-1" style="min-width: 0;">
                      <v-select
                        v-model="row.id_sentido"
                        :items="sentidos"
                        item-text="descricao_sentido"
                        item-value="id_sentido"
                        dense
                        hide-details
                        outlined
                        clearable
                        :label="$t('crudLinhas.form.fences.direction')"
                        :rules="[v => v != null || $t('crudLinhas.validation.required')]"
                      />
                    </v-col>
                    <v-col cols="12" :sm="kmlMapPreviewOpen ? 12 : 6" :md="kmlMapPreviewOpen ? 4 : 2" class="py-1" style="min-width: 0;">
                      <v-text-field
                        v-model.number="row.ordem_parada"
                        type="number"
                        min="1"
                        dense
                        hide-details
                        outlined
                        :label="$t('crudLinhas.form.fences.order')"
                      />
                    </v-col>
                    <v-col cols="12" :sm="kmlMapPreviewOpen ? 12 : 6" :md="kmlMapPreviewOpen ? 4 : 2" class="py-1" style="min-width: 0;">
                      <v-text-field
                        v-model="row.tempo_chegada_minutos"
                        prepend-icon="mdi-clock"
                        suffix="min"
                        dense
                        hide-details
                        outlined
                        :label="$t('crudLinhas.form.fences.arrival')"
                      />
                    </v-col>
                    <v-col cols="12" :sm="kmlMapPreviewOpen ? 12 : 6" :md="kmlMapPreviewOpen ? 4 : 2" class="py-1" style="min-width: 0;">
                      <v-text-field
                        v-if="row.kind === 'point' && !row.matchedExistingId"
                        v-model.number="row.raio_mts"
                        type="number"
                        min="1"
                        dense
                        hide-details
                        outlined
                        :label="$t('geofence.radius')"
                        @focus="ensureFenceRaioDefault(row)"
                      />
                      <v-text-field
                        v-else
                        tabindex="-1"
                        type="text"
                        dense
                        hide-details
                        outlined
                        :value="row.raio_mts ? row.raio_mts : '—'"
                        :label="$t('geofence.radius')"
                        :disabled="!!row.matchedExistingId"
                      />
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-list>
            <div v-if="kmlFenceRows.length > kmlFenceListLimit" class="d-flex justify-center mb-4">
              <v-btn small outlined color="primary" @click="revealMoreKmlFences">
                {{ $t('crudLinhas.kmlAssistant.showMoreFences', { shown: kmlFenceListLimit, total: kmlFenceRows.length }) }}
              </v-btn>
            </div>
            <v-alert v-if="!kmlFenceRows.length" type="info" dense outlined class="mt-2">{{ $t('crudLinhas.kmlAssistant.noFencesInKml') }}</v-alert>

            <v-alert v-if="kmlStep0NextBlockedReason" type="warning" dense outlined class="mt-4">
              {{ kmlStep0NextBlockedReason }}
            </v-alert>
          </v-container>

          <!-- Step 1: line -->
          <v-container v-show="wizardStep === 1">
            <template v-if="!kmlMultiLineMode">
              <v-form ref="form" v-model="formValidado">
                <v-row>
                  <v-col cols="12">
                    <LazySelect
                      ref="assistLinhaSelect"
                      get-url="getLinhaslazy"
                      text="nome_linha"
                      value="id_linha"
                      :label="$t('crudLinhas.kmlAssistant.updateExistingLine')"
                      @input="onAssistantExistingLinhaInput"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="2" md="2">
                    <v-checkbox v-model="isSublinha" :label="$t('crudLinhas.form.fields.sublines')" color="primary" @change="isSublinhaChanged" />
                  </v-col>
                  <v-col cols="12" sm="10" md="10">
                    <LazySelect
                      ref="linhaPrincipalSelect"
                      get-url="getLinhaslazy"
                      text="nome_linha"
                      value="id_linha"
                      :label="$t('crudLinhas.form.fields.selectMainLine')"
                      :rules="[(v) => !!v || !isSublinha || $t('crudLinhas.validation.lineRequired')]"
                      :disabled="!isSublinha"
                      @input="onLinhaPrincipalInput"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="9" md="9">
                    <v-text-field v-model="editedItem.nome_linha" :label="$t('crudLinhas.form.fields.lineName')" :rules="[nomeLinhaRules]" />
                  </v-col>
                  <v-col cols="12" sm="3" md="3">
                    <v-text-field v-model="editedItem.lotacao_prevista" :label="$t('crudLinhas.form.fields.capacityPerTrip')" :rules="[lotacaoRules]" />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <v-textarea
                      v-model="editedItem.descricao_linha"
                      :label="$t('crudLinhas.form.fields.description')"
                      rows="2"
                      auto-grow
                      clearable
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="5" md="4">
                    <v-autocomplete
                      v-model="editedItem.id_empresa"
                      :items="empresas"
                      item-text="razao_social"
                      item-value="id_empresa"
                      clearable
                      :label="$t('crudLinhas.form.fields.selectCompany')"
                      :rules="[(v) => !!v || $t('crudLinhas.validation.companyRequired')]"
                      @change="onAssistenteEmpresaChanged"
                    />
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <v-autocomplete
                      v-model="editedItem.id_cliente"
                      :items="clientesPorEmpresaAssistente(editedItem.id_empresa)"
                      item-text="nome_cliente"
                      item-value="id_cliente"
                      clearable
                      :label="$t('crudLinhas.form.fields.selectClient')"
                      :disabled="!editedItem.id_empresa"
                    />
                  </v-col>
                  <v-col cols="12" sm="3" md="2">
                    <v-text-field v-model="editedItem.cod_linha" :label="$t('crudLinhas.form.fields.lineCode')" :rules="[codLinhaRules]" />
                  </v-col>
                  <v-col cols="12" sm="2" md="2" align-self="center">
                    <v-switch v-model="isCircular" color="primary" :label="$t('crudLinhas.form.fields.circular')" />
                  </v-col>
                  <v-col cols="12" sm="2" md="2" align-self="center">
                    <v-switch v-model="editedItem.linha_intermunicipal" :label="$t('crudLinhas.form.fields.intermunicipal')" color="primary" />
                  </v-col>
                  <v-col cols="12" sm="2" md="2" align-self="center">
                    <v-switch v-model="editedItem.has_eta" :label="$t('crudLinhas.form.fields.eta')" color="primary" />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="6" md="3" align-self="center">
                    <v-text-field v-model="velocidadeMaxima" :label="$t('crudLinhas.form.fields.maxSpeed')" :rules="[velocidadeMaximaRules]" />
                  </v-col>
                  <v-col cols="12" sm="6" md="2" align-self="center">
                    <v-switch v-model="alarmeVelocidadeMaxima" class="ma-2" :label="$t('crudLinhas.form.fields.maxSpeedAlarm')" @change="velocidadeMaximaChanged" />
                  </v-col>
                  <v-col cols="12" sm="6" md="3" align-self="center">
                    <v-text-field
                      v-model="toleranciaForaRota"
                      :label="$t('crudLinhas.form.fields.routeDistanceLimit')"
                      type="number"
                      min="0"
                      :rules="[(v) => (v === null || v >= 0) || $t('crudLinhas.validation.distanceMustBePositive')]"
                    />
                  </v-col>
                  <v-col cols="12" sm="6" md="2" align-self="center">
                    <v-switch v-model="alarmeForaRota" color="primary" :label="$t('crudLinhas.form.fields.outOfRouteAlarm')" />
                  </v-col>
                  <v-col cols="12" sm="6" md="2" align-self="center">
                    <v-switch v-model="isAtiva" color="primary" :label="$t('crudLinhas.form.fields.active')" />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="6" md="3">
                    <v-text-field v-model="limiteTempoParado" :label="$t('crudLinhas.form.fields.stoppedTimeLimit')" :rules="[limiteTempoParadoRules]" type="number" />
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-switch v-model="alarmeTempoParado" color="primary" :label="$t('crudLinhas.form.fields.stoppedTimeAlarm')" />
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-text-field v-model="limiteTempoLargada" :label="$t('crudLinhas.form.fields.departureTimeLimit')" :rules="[limiteTempoLargadaRules]" type="number" />
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-switch v-model="atualizaInicioViagem" color="primary" :label="$t('crudLinhas.form.fields.updateTripStart')" />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="6" md="3">
                    <v-text-field v-model="editedItem.distancia_ida" :label="$t('crudLinhas.form.fields.distanceOutbound')" type="number" suffix="km" />
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-text-field v-model="editedItem.distancia_volta" :label="$t('crudLinhas.form.fields.distanceReturn')" type="number" suffix="km" />
                  </v-col>
                  <v-col cols="12" sm="6" md="6">
                    <v-select
                      v-model="editedItem.id_tipo_aberturas_viagens"
                      :items="tiposAberturasViagens"
                      item-text="descricao_tipo_aberturas_viagens"
                      item-value="id_tipo_aberturas_viagens"
                      :label="$t('crudLinhas.form.fields.tripControlType')"
                    />
                  </v-col>
                </v-row>
              </v-form>
            </template>
            <template v-else>
              <v-tabs v-model="kmlMultiDraftTab" show-arrows class="mb-2">
                <v-tab v-for="gid in kmlActiveGroupsOrdered" :key="'mtab-' + gid">
                  {{ kmlGroupTabLabel(gid) }}
                </v-tab>
              </v-tabs>
              <v-tabs-items v-model="kmlMultiDraftTab">
                <v-tab-item v-for="gid in kmlActiveGroupsOrdered" :key="'mitem-' + gid" class="pt-2">
                  <div v-if="kmlMultiLineDrafts[gid]">
                    <v-row>
                      <v-col cols="12">
                        <LazySelect
                          :ref="'draftAssistLinhaSelect_' + gid"
                          get-url="getLinhaslazy"
                          text="nome_linha"
                          value="id_linha"
                          :label="$t('crudLinhas.kmlAssistant.updateExistingLine')"
                          @input="onMultiDraftAssistantExistingLinhaInput(gid, $event)"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" sm="2" md="2">
                        <v-checkbox
                          v-model="kmlMultiLineDrafts[gid].isSublinha"
                          :label="$t('crudLinhas.form.fields.sublines')"
                          color="primary"
                          @change="multiDraftSublinhaChanged(gid)"
                        />
                      </v-col>
                      <v-col cols="12" sm="10" md="10">
                        <LazySelect
                          :ref="'draftLinhaPrincipalSelect_' + gid"
                          get-url="getLinhaslazy"
                          text="nome_linha"
                          value="id_linha"
                          :label="$t('crudLinhas.form.fields.selectMainLine')"
                          :rules="[(v) => !!v || !kmlMultiLineDrafts[gid].isSublinha || $t('crudLinhas.validation.lineRequired')]"
                          :disabled="!kmlMultiLineDrafts[gid].isSublinha"
                          @input="onMultiDraftLinhaPrincipalInput(gid, $event)"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" sm="9" md="9">
                        <v-text-field v-model="kmlMultiLineDrafts[gid].editedItem.nome_linha" :label="$t('crudLinhas.form.fields.lineName')" />
                      </v-col>
                      <v-col cols="12" sm="3" md="3">
                        <v-text-field v-model="kmlMultiLineDrafts[gid].editedItem.lotacao_prevista" :label="$t('crudLinhas.form.fields.capacityPerTrip')" type="number" />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <v-textarea
                          v-model="kmlMultiLineDrafts[gid].editedItem.descricao_linha"
                          :label="$t('crudLinhas.form.fields.description')"
                          rows="2"
                          auto-grow
                          clearable
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" sm="6" md="4">
                        <v-autocomplete
                          v-model="kmlMultiLineDrafts[gid].editedItem.id_empresa"
                          :items="empresas"
                          item-text="razao_social"
                          item-value="id_empresa"
                          clearable
                          :label="$t('crudLinhas.form.fields.selectCompany')"
                          @change="onAssistenteEmpresaChangedDraft(gid)"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" md="4">
                        <v-autocomplete
                          v-model="kmlMultiLineDrafts[gid].editedItem.id_cliente"
                          :items="clientesPorEmpresaAssistente(kmlMultiLineDrafts[gid].editedItem.id_empresa)"
                          item-text="nome_cliente"
                          item-value="id_cliente"
                          clearable
                          :label="$t('crudLinhas.form.fields.selectClient')"
                          :disabled="!kmlMultiLineDrafts[gid].editedItem.id_empresa"
                        />
                      </v-col>
                      <v-col cols="12" sm="3" md="2">
                        <v-text-field v-model="kmlMultiLineDrafts[gid].editedItem.cod_linha" :label="$t('crudLinhas.form.fields.lineCode')" />
                      </v-col>
                      <v-col cols="12" sm="2" md="2" align-self="center">
                        <v-switch v-model="kmlMultiLineDrafts[gid].isCircular" color="primary" :label="$t('crudLinhas.form.fields.circular')" />
                      </v-col>
                      <v-col cols="12" sm="2" md="2" align-self="center">
                        <v-switch v-model="kmlMultiLineDrafts[gid].editedItem.linha_intermunicipal" :label="$t('crudLinhas.form.fields.intermunicipal')" color="primary" />
                      </v-col>
                      <v-col cols="12" sm="2" md="2" align-self="center">
                        <v-switch v-model="kmlMultiLineDrafts[gid].editedItem.has_eta" :label="$t('crudLinhas.form.fields.eta')" color="primary" />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" sm="6" md="3" align-self="center">
                        <v-text-field v-model.number="kmlMultiLineDrafts[gid].velocidadeMaxima" type="number" :label="$t('crudLinhas.form.fields.maxSpeed')" />
                      </v-col>
                      <v-col cols="12" sm="6" md="2" align-self="center">
                        <v-switch
                          v-model="kmlMultiLineDrafts[gid].alarmeVelocidadeMaxima"
                          class="ma-2"
                          :label="$t('crudLinhas.form.fields.maxSpeedAlarm')"
                          @change="multiDraftVelocidadeMaximaChanged(gid)"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" md="3" align-self="center">
                        <v-text-field
                          v-model.number="kmlMultiLineDrafts[gid].toleranciaForaRota"
                          :label="$t('crudLinhas.form.fields.routeDistanceLimit')"
                          type="number"
                          min="0"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" md="2" align-self="center">
                        <v-switch v-model="kmlMultiLineDrafts[gid].alarmeForaRota" color="primary" :label="$t('crudLinhas.form.fields.outOfRouteAlarm')" />
                      </v-col>
                      <v-col cols="12" sm="6" md="2" align-self="center">
                        <v-switch v-model="kmlMultiLineDrafts[gid].isAtiva" color="primary" :label="$t('crudLinhas.form.fields.active')" />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" sm="6" md="3">
                        <v-text-field v-model.number="kmlMultiLineDrafts[gid].limiteTempoParado" :label="$t('crudLinhas.form.fields.stoppedTimeLimit')" type="number" />
                      </v-col>
                      <v-col cols="12" sm="6" md="3">
                        <v-switch v-model="kmlMultiLineDrafts[gid].alarmeTempoParado" color="primary" :label="$t('crudLinhas.form.fields.stoppedTimeAlarm')" />
                      </v-col>
                      <v-col cols="12" sm="6" md="3">
                        <v-text-field v-model.number="kmlMultiLineDrafts[gid].limiteTempoLargada" :label="$t('crudLinhas.form.fields.departureTimeLimit')" type="number" />
                      </v-col>
                      <v-col cols="12" sm="6" md="3">
                        <v-switch v-model="kmlMultiLineDrafts[gid].atualizaInicioViagem" color="primary" :label="$t('crudLinhas.form.fields.updateTripStart')" />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" sm="6" md="3">
                        <v-text-field v-model="kmlMultiLineDrafts[gid].editedItem.distancia_ida" :label="$t('crudLinhas.form.fields.distanceOutbound')" type="number" suffix="km" />
                      </v-col>
                      <v-col cols="12" sm="6" md="3">
                        <v-text-field v-model="kmlMultiLineDrafts[gid].editedItem.distancia_volta" :label="$t('crudLinhas.form.fields.distanceReturn')" type="number" suffix="km" />
                      </v-col>
                      <v-col cols="12" sm="6" md="6">
                        <v-select
                          v-model="kmlMultiLineDrafts[gid].editedItem.id_tipo_aberturas_viagens"
                          :items="tiposAberturasViagens"
                          item-text="descricao_tipo_aberturas_viagens"
                          item-value="id_tipo_aberturas_viagens"
                          :label="$t('crudLinhas.form.fields.tripControlType')"
                        />
                      </v-col>
                    </v-row>
                  </div>
                </v-tab-item>
              </v-tabs-items>
            </template>
          </v-container>

          <!-- Step 2: shifts -->
          <div v-show="wizardStep === 2">
            <v-container>
              <v-alert v-if="kmlMultiLineMode" type="info" dense outlined class="mb-4">
                {{ $t('crudLinhas.kmlAssistant.sameShiftsAllLines') }}
              </v-alert>
              <v-form ref="formTurnosPassageiros" v-model="formValidadoTurnosPassageiros">
                <v-row>
                  <v-col cols="12" sm="5" md="5">
                    <v-autocomplete
                      v-model="itemTurnosPassageiros.id_turno"
                      :items="turnosPassageiros"
                      item-text="descricao_turno"
                      item-value="id_turno"
                      :label="$t('crudLinhas.form.shifts.self')"
                    />
                  </v-col>
                  <v-col cols="12" sm="5" md="5">
                    <v-text-field
                      v-model="itemTurnosPassageiros.lotacao"
                      :label="$t('crudLinhas.form.shifts.passengersCapacity')"
                      type="number"
                      :rules="[(v) => v >= 0 || $t('crudLinhas.validation.capacityMustBeGreaterThanZero')]"
                    />
                  </v-col>
                  <v-col cols="12" sm="2" md="2">
                    <v-btn color="primary" dark small class="mb-2" fab :disabled="disableInsertTurnosPassageiros" @click="saveTurnosPassageiros">
                      <v-icon v-if="!editingTurnosPassageiros">mdi-plus</v-icon>
                      <v-icon v-else>mdi-pencil</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
              <merus-data-table
                :headers="headersTurnosPassageiros"
                :items="turnosPassageirosLinha"
                :loading="false"
                :loading-text="$t('crudLinhas.loading')"
                :default-multi-sort="defaultMultiSortTurnosPassageiros"
              >
                <template v-slot:toolbar>
                  <cadastro-toolbar
                    :title="$t('crudLinhas.form.shifts.title')"
                    :token="token"
                    :items="turnosPassageirosLinha"
                    :csv-title="$t('crudLinhas.form.shifts.csvTitle')"
                    :pdf-title="$t('crudLinhas.form.shifts.pdfTitle')"
                    :pdf-headers="headersTurnosPassageiros"
                    :pdf-filename="$t('crudLinhas.form.shifts.pdfFilename')"
                    hide-search
                  />
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-icon small class="mr-2" color="green" @click="editItemTurnosPassageiros(item)">mdi-pencil</v-icon>
                  <v-icon small class="mr-2" color="red" @click="deleteItemTurnosPassageiros(item)">mdi-delete</v-icon>
                </template>
              </merus-data-table>
            </v-container>
          </div>
        </template>
      </v-card-text>
        </v-col>
        <v-col
          v-if="kmlGeometryPreview.open"
          cols="12"
          md="5"
          lg="4"
          class="linha-assistente-wizard-col-map d-flex flex-column fill-height"
        >
          <linha-assistente-kml-map-overlay
            :title="kmlMapOverlayToolbarTitle"
            :scope="kmlGeometryPreview.scope || 'route'"
            :map-key="kmlGeometryPreview.mapKey"
            :preview="kmlGeometryPreview"
            :fence-layers="kmlMapFenceLayers"
            :tile-url="tileUrl"
            :tile-attribution="tileAttribution"
            :show-route-highlight-select="kmlMapOverlayShowRouteHighlightSelect"
            :route-highlight-items="kmlGeometryPreviewRouteSelectItems"
            :route-highlight-index="kmlGeometryPreview.fenceMapRouteHighlightIndex"
            @input="onKmlMapOverlayInput"
            @update:routeHighlightIndex="onFenceMapRouteHighlightChange"
            @select-fence="onKmlMapSelectFence"
            @select-route="onKmlMapSelectRoute"
            :route-preview-focus-index="kmlMapRoutePreviewFocusIndex"
            :highlighted-route-row-index="kmlMapFenceRouteHighlightRowIndex"
          />
        </v-col>
      </v-row>
    </v-card>
  </v-dialog>

  <v-dialog v-model="rotaCodDupDialogOpen" persistent max-width="440" :z-index="10000">
    <v-card>
      <v-card-title class="subtitle-1 pa-4 pb-2">
        {{
          rotaCodDupStep === 'choose'
            ? $t('crudLinhas.kmlAssistant.rotaCodDuplicateTitle')
            : $t('crudLinhas.kmlAssistant.rotaCodDuplicateNewCodeStepTitle')
        }}
      </v-card-title>
      <v-card-text class="pt-0 pb-2">
        <template v-if="rotaCodDupStep === 'choose'">
          <p class="body-2 mb-0">{{ $t('crudLinhas.kmlAssistant.rotaCodDuplicateBody') }}</p>
          <div v-if="rotaCodDupExisting && rotaCodDupExisting.nome_rota" class="caption grey--text mt-2">
            {{ rotaCodDupExisting.nome_rota }}
            <span v-if="rotaCodDupExisting.id_rota != null"> · id {{ rotaCodDupExisting.id_rota }}</span>
          </div>
        </template>
        <template v-else>
          <v-text-field
            v-model="rotaCodDupNewCodInput"
            outlined
            dense
            autofocus
            :label="$t('crudLinhas.kmlAssistant.rotaCodDuplicateNewCodeField')"
            :hint="$t('crudLinhas.kmlAssistant.rotaCodDuplicateNewCodeHint')"
            persistent-hint
          />
        </template>
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <template v-if="rotaCodDupStep === 'choose'">
          <v-btn text @click="rotaCodDupCancel">{{ $t('button.cancel') }}</v-btn>
          <v-spacer />
          <v-btn color="primary" outlined @click="rotaCodDupGoToChangeCode">
            {{ $t('crudLinhas.kmlAssistant.rotaCodDuplicateChangeCode') }}
          </v-btn>
          <v-btn color="primary" @click="rotaCodDupConfirmUpdate">
            {{ $t('crudLinhas.kmlAssistant.rotaCodDuplicateAgree') }}
          </v-btn>
        </template>
        <template v-else>
          <v-btn text @click="rotaCodDupBackToChoice">{{ $t('crudLinhas.kmlAssistant.rotaCodDuplicateBack') }}</v-btn>
          <v-spacer />
          <v-btn color="primary" @click="rotaCodDupConfirmNew">
            {{ $t('crudLinhas.kmlAssistant.rotaCodDuplicateConfirmNew') }}
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
  </div>
</template>

<script>
import EventBus from '@/utils/eventBus';
import apiController from '@/utils/apiController';
import { validador } from '@/constants';
import translateDbField from '@/utils/translateDbField';
import MerusDataTable from '@/components/Tables/MerusDataTable.vue';
import CadastroToolbar from '@/components/Toolbars/CadastroToolbar.vue';
import LinhaAssistenteKmlMapOverlay from '@/components/Dialogs/LinhaAssistenteKmlMapOverlay.vue';
import LazySelect from '@/components/Selects/LazySelect.vue';
import linhaAssistentePersistMixin from '@/mixins/linhaAssistentePersistMixin';
import {
  lineStringFeatureToMerusShape,
  polygonFeatureToMerusShape,
  sanitizeCercaCodForInsert,
} from '@/utils/kmlLineAssistantGeo';
import {
  findExistingCercaAt,
  partitionCercasCatalogByShape,
  ringCentroidLngLat,
} from '@/utils/matchCercaByLocation';
import {
  findExistingRotaForFeature,
  slimRotasCatalogForMatch,
  yieldToMain,
} from '@/utils/kmlRouteEndpointMatch';
import L from 'leaflet';

const KML_SINGLE_GROUP_ID = '__single__';
/** How many KML route/fence form rows to mount initially (avoids multi-second Vue stalls). */
const KML_LIST_INITIAL_LIMIT = 40;
const KML_LIST_PAGE_SIZE = 40;
const KML_MATCH_CHUNK_SIZE = 40;

export default {
  name: 'LinhaAssistenteDialog',
  components: {
    MerusDataTable,
    CadastroToolbar,
    LinhaAssistenteKmlMapOverlay,
    LazySelect,
  },
  mixins: [linhaAssistentePersistMixin],
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      user: null,
      token: null,
      recursos: [],
      empresasPermitidas: [],
      wizardStep: 0,
      savingLine: false,
      operacao: 0,
      editedIndex: -1,
      formValidado: true,
      formValidadoKml: true,
      formValidadoTurnosPassageiros: false,
      defaultOrdemStart: 1,
      defaultRouteTipo: null,
      defaultRouteSentido: null,
      defaultFenceSentido: null,
      defaultFenceTipo: null,
      kmlFile: null,
      parsingKml: false,
      kmlRouteRows: [],
      kmlFenceRows: [],
      cercasCatalog: [],
      rotasCatalog: [],
      kmlRouteListLimit: KML_LIST_INITIAL_LIMIT,
      kmlFenceListLimit: KML_LIST_INITIAL_LIMIT,
      sentidos: [],
      tiposRota: [],
      tiposCerca: [],
      tiposAberturasViagens: [],
      empresas: [],
      clientes: [],
      /** Cache id_linha -> registro completo (getalllinhas paginado por id). */
      linhaDetalhePorId: {},
      isSublinha: false,
      linhaPrincipal: null,
      isAtiva: true,
      isCircular: false,
      velocidadeMaxima: 0,
      limiteTempoParado: 0,
      alarmeTempoParado: false,
      alarmeForaRota: false,
      alarmeVelocidadeMaxima: false,
      limiteTempoLargada: null,
      atualizaInicioViagem: false,
      toleranciaForaRota: null,
      editedItem: {
        id_linha: null,
        id_empresa: null,
        nome_linha: null,
        descricao_linha: null,
        cod_linha: null,
        id_rota: null,
        nome_rota: null,
        shape: null,
        velocidade_maxima: null,
        alarme_velocidade_maxima: null,
        tolerancia_fora_rota: null,
        alarme_fora_rota: null,
        circular: null,
        distancia_ida: null,
        distancia_volta: null,
        limite_tempo_parado: null,
        alarme_tempo_parado: null,
        tempo_maximo_largada: null,
        atualiza_inicio_viagem: null,
        id_linha_principal: null,
        linha_intermunicipal: null,
        lotacao_prevista: null,
        has_eta: null,
        id_tipo_aberturas_viagens: null,
        cod_integracao_prodata_tz: null,
        id_cliente: null,
      },
      defaultItem: {
        id_linha: null,
        id_empresa: null,
        nome_linha: null,
        descricao_linha: null,
        cod_linha: null,
        id_rota: null,
        nome_rota: null,
        shape: null,
        velocidade_maxima: null,
        alarme_velocidade_maxima: null,
        tolerancia_fora_rota: null,
        alarme_fora_rota: null,
        circular: null,
        distancia_ida: null,
        distancia_volta: null,
        limite_tempo_parado: null,
        alarme_tempo_parado: null,
        tempo_maximo_largada: null,
        atualiza_inicio_viagem: null,
        id_linha_principal: null,
        linha_intermunicipal: null,
        lotacao_prevista: null,
        has_eta: null,
        id_tipo_aberturas_viagens: null,
        cod_integracao_prodata_tz: null,
        id_cliente: null,
      },
      assistantExistingLinha: null,
      existingRotasLinhaByLinha: [],
      existingCercasLinhaByLinha: [],
      rotasLinha: [],
      cercasLinha: [],
      editedRotas: false,
      editedCercas: false,
      editedTurnosPassageiros: false,
      loadingInsertCercas: false,
      loadingInsertRotas: false,
      loadingInsertTurnosPassageiros: false,
      loadingInsertSublinhas: false,
      turnosPassageiros: [],
      turnosPassageirosLinha: [],
      unidadesLinhaIds: [],
      itemTurnosPassageiros: { id_turno: null, lotacao: null },
      defaultItemTurnosPassageiros: { id_turno: null, lotacao: null },
      editingTurnosPassageiros: false,
      disableInsertTurnosPassageiros: false,
      kmlMultiLineMode: false,
      kmlLineGroups: [],
      kmlMultiLineDrafts: {},
      kmlMultiDraftTab: 0,
      kmlGeometryPreview: {
        open: false,
        scope: null,
        index: null,
        title: '',
        mapKey: 0,
        zoom: 15,
        center: [-14.235, -51.9253],
        geojsonFeature: null,
        geojsonStyle: {},
        secondaryRoutesGeojson: null,
        secondaryRoutesStyle: {},
        fenceMapRouteHighlightIndex: null,
        fenceMapHighlightedRouteFeature: null,
        fenceMapHighlightedRouteStyle: {},
        circle: null,
        markerLatLng: null,
      },
      tileUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      tileAttribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      rotaCodDupDialogOpen: false,
      rotaCodDupExisting: null,
      rotaCodDupDuplicateCod: '',
      rotaCodDupNewCodInput: '',
      rotaCodDupStep: 'choose',
      rotaCodDupResolveFn: null,
      rotaCodDupRejectFn: null,
      /** When saving multi-KML groups, mixin reads this instead of editedItem.id_linha for bulk/vigência paths. */
      persistAssistantUpdatingExistingOverride: null,
    };
  },
  computed: {
    kmlRouteRowsVisible() {
      return this.kmlRouteRows.slice(0, this.kmlRouteListLimit);
    },
    kmlFenceRowsVisible() {
      return this.kmlFenceRows.slice(0, this.kmlFenceListLimit);
    },
    kmlIdentifiedFenceCount() {
      return this.kmlFenceRows.filter((r) => r.fromKml).length;
    },
    kmlFenceSelectedCount() {
      return this.kmlFenceRows.filter((r) => r.selected).length;
    },
    allKmlFencesSelected() {
      return this.kmlFenceRows.length > 0 && this.kmlFenceSelectedCount === this.kmlFenceRows.length;
    },
    someKmlFencesSelected() {
      return this.kmlFenceSelectedCount > 0;
    },
    kmlActiveGroupsOrdered() {
      if (!this.kmlMultiLineMode) return [];
      const order = [];
      const seen = new Set();
      const pushRow = (row) => {
        this.normalizeRowGroupIdsArray(row).forEach((gid) => {
          if (gid == null || gid === '' || seen.has(gid)) return;
          seen.add(gid);
          order.push(gid);
        });
      };
      this.kmlRouteRows.forEach((r) => pushRow(r));
      this.kmlFenceRows.forEach((f) => pushRow(f));
      return order;
    },
    canUseAssistant() {
      if (!this.user) return false;
      if (this.user.is_superadmin === 1) return true;
      const r = Array.isArray(this.recursos) ? this.recursos : [];
      return (
        r.some((x) => x.rota === '/api/v1.0/insertlinha') &&
        r.some((x) => x.rota === '/api/v1.0/insertrota') &&
        r.some((x) => x.rota === '/api/v1.0/insertcerca')
      );
    },
    kmlRouteCatalogsUnavailable() {
      return !this.tiposRota.length || !this.sentidos.length;
    },
    kmlEmpresasUnavailable() {
      return !Array.isArray(this.empresas) || !this.empresas.length;
    },
    assistantUpdatingExistingLinha() {
      if (this.persistAssistantUpdatingExistingOverride != null) {
        return !!this.persistAssistantUpdatingExistingOverride;
      }
      return !!(this.editedItem && this.editedItem.id_linha);
    },
    kmlStep0NextBlockedReason() {
      if (this.wizardStep !== 0 || !this.kmlRouteRows.length) return null;
      if (!this.kmlRouteRowsValid) {
        return this.$t('crudLinhas.kmlAssistant.fillAllRouteFields');
      }
      if (this.kmlFenceRows.length && !this.kmlFenceRowsValid) {
        return this.$t('crudLinhas.kmlAssistant.fillAllFenceFields');
      }
      if (this.kmlMultiLineMode) {
        return this.getKmlMultiLineValidationError();
      }
      if (!this.kmlRouteRows.some((r) => this.isKmlPrimaryRota(r))) {
        return this.$t('crudLinhas.wizard.missingPrimaryRoute');
      }
      return null;
    },
    canGoNext() {
      if (this.wizardStep === 0) {
        const base = this.kmlRouteRows.length > 0 && this.kmlRouteRowsValid && this.kmlFenceRowsValid;
        if (!base) return false;
        if (this.kmlMultiLineMode) {
          if (this.getKmlMultiLineValidationError()) return false;
        } else if (!this.kmlRouteRows.some((r) => this.isKmlPrimaryRota(r))) {
          return false;
        }
        return true;
      }
      return true;
    },
    kmlFenceRowsValid() {
      if (!this.kmlFenceRows.length) return true;
      return this.kmlFenceRows.every((f) => {
        const usesExisting = !!f.matchedExistingId;
        if (!usesExisting) {
          if (!f.cod_cerca || !String(f.cod_cerca).trim() || !f.nome_cerca || !String(f.nome_cerca).trim()) {
            return false;
          }
        }
        if (f.id_tipo_cerca == null || f.id_sentido == null) return false;
        if (f.kind === 'point' && !usesExisting && (!f.raio_mts || f.raio_mts <= 0)) return false;
        if (f.ordem_parada == null || f.ordem_parada === '' || Number(f.ordem_parada) < 1) return false;
        {
          const t = f.tempo_chegada_minutos;
          if (t != null && t !== '') {
            const n = Number(t);
            if (!Number.isFinite(n) || n < 0) return false;
          }
        }
        if (f.kind === 'existing' && !usesExisting) return false;
        return true;
      });
    },
    kmlGeometryPreviewRouteSelectItems() {
      return this.kmlRouteRows.map((row, i) => {
        const name = (row.nome_rota && String(row.nome_rota).trim()) || row.kmlLabel || '—';
        const hasGeom = this.kmlRouteRowHasMapGeometry(row);
        const suffix = hasGeom ? '' : ` (${this.$t('crudLinhas.kmlAssistant.noMapGeometry')})`;
        return {
          text: `${i + 1}. ${name}${suffix}`,
          value: i,
          disabled: !hasGeom,
        };
      });
    },
    /** v-model bridge: which KML route is highlighted on the stops (fence) map. */
    fenceMapRouteHighlightSelect: {
      get() {
        return this.kmlGeometryPreview.fenceMapRouteHighlightIndex;
      },
      set(val) {
        this.onFenceMapRouteHighlightChange(val);
      },
    },
    kmlMapOverlayShowRouteHighlightSelect() {
      return (
        this.kmlGeometryPreview.open &&
        this.kmlGeometryPreview.scope === 'fence' &&
        this.kmlRouteRows.length > 1
      );
    },
    /** Row index of the bold route on the map (route preview mode). */
    kmlMapRoutePreviewFocusIndex() {
      if (!this.kmlGeometryPreview.open || this.kmlGeometryPreview.scope !== 'route') return null;
      return this.kmlGeometryPreview.index;
    },
    /** Row index of the highlighted route on the map (fence / stops preview mode). */
    kmlMapFenceRouteHighlightRowIndex() {
      if (!this.kmlGeometryPreview.open || this.kmlGeometryPreview.scope !== 'fence') return null;
      return this.kmlGeometryPreview.fenceMapRouteHighlightIndex;
    },
    /** True while KML map preview is open; step 0 grids stack fields vertically in the narrower left pane. */
    kmlMapPreviewOpen() {
      return this.kmlGeometryPreview.open;
    },
    /** Scroll area under toolbar; map lives in a sibling column when preview is open. */
    assistantMainScrollStyle() {
      return {
        overflowY: 'auto',
        minHeight: 0,
        flex: '1 1 auto',
      };
    },
    /** Keeps map header in sync when the user edits route/stop fields while the preview is open. */
    kmlMapOverlayToolbarTitle() {
      if (!this.kmlGeometryPreview.open) return this.kmlGeometryPreview.title || '';
      const scope = this.kmlGeometryPreview.scope;
      if (scope === 'route') {
        const idx = this.kmlGeometryPreview.index;
        const row = idx != null ? this.kmlRouteRows[idx] : null;
        const name = row ? row.nome_rota || row.kmlLabel || '—' : '—';
        return this.$t('crudLinhas.kmlAssistant.mapPreviewRoute', { name });
      }
      if (scope === 'fence') {
        const idx = this.kmlGeometryPreview.index;
        const row = idx != null ? this.kmlFenceRows[idx] : null;
        const name = row ? this.kmlFenceMapPreviewTitleName(row) : '—';
        return this.$t('crudLinhas.kmlAssistant.mapPreviewFence', { name });
      }
      return this.kmlGeometryPreview.title || '';
    },
    kmlMapFenceLayers() {
      if (!this.kmlGeometryPreview.open) return [];
      const scope = this.kmlGeometryPreview.scope;
      const focusIdx = this.kmlGeometryPreview.index;
      const MAX_LAYERS = 200;
      const candidates = [];
      for (let idx = 0; idx < this.kmlFenceRows.length; idx++) {
        const row = this.kmlFenceRows[idx];
        if (!this.kmlFenceRowHasMapGeometry(row)) continue;
        const base = this.fenceRowToMapLayer(row);
        if (!base) continue;
        const focused = scope === 'fence' && focusIdx === idx;
        const item = { id: idx, tooltipText: this.kmlFenceMapStopTooltipText(row), focused };
        if (base.geojsonFeature) {
          const f = base.geojsonFeature;
          item.geojsonFeature = {
            type: 'Feature',
            geometry: f.geometry,
            properties: {
              ...(f.properties && typeof f.properties === 'object' ? { ...f.properties } : {}),
              kmlFenceRowIndex: idx,
              kmlFenceTooltip: item.tooltipText,
            },
          };
          const st = focused ? base.geojsonStyle : this.mutedFenceGeojsonStyle(base.geojsonStyle);
          item.geojsonStyle = { ...st, className: 'kml-map-stop-layer' };
        }
        if (base.circle) {
          item.circle = focused
            ? {
                latLng: base.circle.latLng,
                radius: base.circle.radius,
                color: '#2e7d32',
                fillColor: '#81c784',
                fillOpacity: 0.35,
                weight: 2,
              }
            : {
                latLng: base.circle.latLng,
                radius: base.circle.radius,
                color: '#81c784',
                fillColor: '#c8e6c9',
                fillOpacity: 0.22,
                weight: 1,
              };
        }
        if (base.markerLatLng) {
          item.markerLatLng = base.markerLatLng;
        }
        candidates.push(item);
      }
      if (candidates.length <= MAX_LAYERS) {
        return candidates.map((c) => {
          const rest = { ...c };
          delete rest.focused;
          return rest;
        });
      }
      const focusedItems = candidates.filter((c) => c.focused);
      const others = candidates.filter((c) => !c.focused).slice(0, Math.max(0, MAX_LAYERS - focusedItems.length));
      return focusedItems.concat(others).map((c) => {
        const rest = { ...c };
        delete rest.focused;
        return rest;
      });
    },
    kmlRouteRowsValid() {
      return this.kmlRouteRows.every((row) => {
        const usesExisting = !!row.matchedExistingId;
        if (!usesExisting) {
          if (!row.cod_rota || !String(row.cod_rota).trim()) return false;
          if (!row.nome_rota || !String(row.nome_rota).trim()) return false;
          if (row.id_empresa == null) return false;
        } else {
          if (!this.kmlRouteCodFieldDisplay(row) || !this.kmlRouteNomeFieldDisplay(row)) return false;
          if (row.id_empresa == null) return false;
        }
        return row.id_tipo_rota != null && row.id_sentido != null;
      });
    },
    wizardStepTitle() {
      const keys = [
        'crudLinhas.kmlAssistant.stepKml',
        'crudLinhas.wizard.stepLine',
        'crudLinhas.wizard.stepShifts'];
      return this.$t(keys[this.wizardStep] || keys[0]);
    },
    headersTurnosPassageiros() {
      return [
        { text: this.$t('crudLinhas.headers.shifts.code'), value: 'cod_turno' },
        { text: this.$t('crudLinhas.headers.shifts.description'), value: 'descricao_turno' },
        { text: this.$t('crudLinhas.headers.shifts.capacity'), value: 'lotacao' },
        { text: this.$t('crudLinhas.headers.shifts.actions'), value: 'actions', sortable: false }];
    },
    defaultMultiSortTurnosPassageiros() {
      return [{ value: 'cod_turno', options: { text: this.$t('system.labels.increasing'), value: 1 } }];
    },
    nomeLinhaRules() {
      if (!validador(this.editedItem.nome_linha, 1, 100, 2, 1)) {
        return this.$t('crudLinhas.validation.lineNameRequired');
      }
      return true;
    },
    codLinhaRules() {
      if (!validador(this.editedItem.cod_linha, 1, 50, 0, 1)) {
        return this.$t('crudLinhas.validation.lineCodeRequired');
      }
      return true;
    },
    lotacaoRules() {
      if (this.editedItem.lotacao_prevista && this.editedItem.lotacao_prevista <= 0) {
        return this.$t('crudLinhas.validation.mustBeGreaterThanZero');
      }
      return true;
    },
    velocidadeMaximaRules() {
      if (this.velocidadeMaxima == null || this.velocidadeMaxima.length === 0) {
        return this.$t('crudLinhas.validation.maxSpeedRequired');
      }
      if (this.alarmeVelocidadeMaxima === true && this.velocidadeMaxima < 1) {
        return this.$t('crudLinhas.validation.maxSpeedMustBeGreaterThanZero');
      }
      return true;
    },
    limiteTempoParadoRules() {
      if (this.limiteTempoParado === null) return true;
      if (this.limiteTempoParado.length === 0) {
        return this.$t('crudLinhas.validation.stoppedTimeLimitRequired');
      }
      if (this.alarmeTempoParado === true && this.limiteTempoParado < 1) {
        return this.$t('crudLinhas.validation.stoppedTimeLimitMustBeGreaterThanZero');
      }
      return true;
    },
    limiteTempoLargadaRules() {
      if (!this.atualizaInicioViagem) return true;
      if (this.atualizaInicioViagem && (this.limiteTempoLargada === null || this.limiteTempoLargada.length === 0)) {
        return this.$t('crudLinhas.validation.departureTimeLimitRequired');
      }
      if (this.limiteTempoLargada != null && this.limiteTempoLargada !== '' && this.limiteTempoLargada < 1) {
        return this.$t('crudLinhas.validation.departureTimeLimitMustBeGreaterThanZero');
      }
      return true;
    },
  },
  watch: {
    value(v) {
      if (v) {
        this.onOpen();
      }
    },
    kmlMultiLineMode(val) {
      this.onKmlMultiLineModeChange(val);
    },
    wizardStep(step) {
      if (step !== 0) this.closeKmlGeometryPreview();
    },
  },
  created() {
    this.user = JSON.parse(sessionStorage.getItem('user') || 'null');
    this.token = sessionStorage.getItem('api');
  },
  methods: {
    onOpen() {
      this.closeKmlGeometryPreview();
      this.wizardStep = 0;
      this.defaultOrdemStart = 1;
      this.defaultRouteTipo = null;
      this.defaultRouteSentido = null;
      this.defaultFenceSentido = null;
      this.defaultFenceTipo = null;
      this.kmlFile = null;
      this.kmlRouteRows = [];
      this.kmlFenceRows = [];
      this.kmlRouteListLimit = KML_LIST_INITIAL_LIMIT;
      this.kmlFenceListLimit = KML_LIST_INITIAL_LIMIT;
      this.rotasLinha = [];
      this.cercasLinha = [];
      this.editedRotas = false;
      this.editedCercas = false;
      this.editedTurnosPassageiros = false;
      this.turnosPassageirosLinha = [];
      this.unidadesLinhaIds = [];
      this.itemTurnosPassageiros = { ...this.defaultItemTurnosPassageiros };
      this.editingTurnosPassageiros = false;
      this.editedItem = { ...this.defaultItem };
      this.isSublinha = false;
      this.linhaPrincipal = null;
      this.resetLinhaSelectRef('linhaPrincipalSelect');
      this.isAtiva = true;
      this.isCircular = false;
      this.velocidadeMaxima = 0;
      this.limiteTempoParado = 0;
      this.alarmeTempoParado = false;
      this.alarmeForaRota = false;
      this.alarmeVelocidadeMaxima = false;
      this.atualizaInicioViagem = false;
      this.limiteTempoLargada = 0;
      this.toleranciaForaRota = null;
      this.persistAssistantUpdatingExistingOverride = null;
      this.populaRecursos();
      this.populaEmpresas();
      this.populaClientes();
      this.populaSentidos();
      this.populaTiposRota();
      this.populaTiposCerca();
      this.populaTiposAberturasViagens();
      this.populaTurnos();
      this.populaCercasCatalog();
      this.populaRotasCatalog();
      this.kmlMultiLineMode = false;
      this.kmlMultiLineDrafts = {};
      this.kmlMultiDraftTab = 0;
      this.assistantExistingLinha = null;
      this.resetLinhaSelectRef('assistLinhaSelect');
      this.existingRotasLinhaByLinha = [];
      this.existingCercasLinhaByLinha = [];
      this.resetKmlLineGroups();
    },
    resetKmlLineGroups() {
      this.kmlLineGroups = [
        { id: 'g1', label: this.$t('crudLinhas.kmlAssistant.lineGroupLabel', { n: 1 }) },
        { id: 'g2', label: this.$t('crudLinhas.kmlAssistant.lineGroupLabel', { n: 2 }) }];
    },
    addKmlLineGroup() {
      const n = this.kmlLineGroups.length + 1;
      const id = `g${Date.now()}`;
      this.kmlLineGroups.push({
        id,
        label: this.$t('crudLinhas.kmlAssistant.lineGroupLabel', { n }),
      });
    },
    normalizeRowGroupIdsArray(row) {
      if (!row) return [];
      let raw = [];
      if (Array.isArray(row.kmlGroupIds) && row.kmlGroupIds.length) {
        raw = row.kmlGroupIds;
      } else if (row.kmlGroupId != null && row.kmlGroupId !== '') {
        raw = [row.kmlGroupId];
      }
      const seen = new Set();
      const out = [];
      for (let i = 0; i < raw.length; i++) {
        const id = raw[i];
        if (id == null || id === '' || seen.has(id)) continue;
        seen.add(id);
        out.push(id);
      }
      return out;
    },
    rowBelongsToKmlGroup(row, gid) {
      return this.normalizeRowGroupIdsArray(row).includes(gid);
    },
    /** v-select may bind id_tipo_rota as string; tipo principal is 1 */
    isKmlPrimaryRota(row) {
      return row != null && Number(row.id_tipo_rota) === 1;
    },
    onKmlMultiLineModeChange(isMulti) {
      if (isMulti) {
        this.assistantExistingLinha = null;
        this.resetLinhaSelectRef('assistLinhaSelect');
        this.existingRotasLinhaByLinha = [];
        this.existingCercasLinhaByLinha = [];
        this.resetAssistantLineFormOnly();
      }
      if (!isMulti) {
        this.kmlRouteRows = this.kmlRouteRows.map((r) => {
          const next = { ...r, kmlGroupIds: [KML_SINGLE_GROUP_ID] };
          delete next.kmlGroupId;
          return next;
        });
        this.kmlFenceRows = this.kmlFenceRows.map((r) => {
          const next = { ...r, kmlGroupIds: [KML_SINGLE_GROUP_ID] };
          delete next.kmlGroupId;
          return next;
        });
        this.kmlMultiLineDrafts = {};
        return;
      }
      const first = this.kmlLineGroups[0] && this.kmlLineGroups[0].id;
      if (!first) return;
      const valid = new Set(this.kmlLineGroups.map((g) => g.id));
      this.kmlRouteRows = this.kmlRouteRows.map((r) => {
        let ids = this.normalizeRowGroupIdsArray(r).filter((id) => valid.has(id));
        if (!ids.length) ids = [first];
        const next = { ...r, kmlGroupIds: ids };
        delete next.kmlGroupId;
        return next;
      });
      this.kmlFenceRows = this.kmlFenceRows.map((r) => {
        let ids = this.normalizeRowGroupIdsArray(r).filter((id) => valid.has(id));
        if (!ids.length) ids = [first];
        const next = { ...r, kmlGroupIds: ids };
        delete next.kmlGroupId;
        return next;
      });
    },
    kmlGroupTabLabel(gid) {
      const g = this.kmlLineGroups.find((x) => x.id === gid);
      return g ? g.label : String(gid);
    },
    getKmlMultiLineValidationError() {
      if (!this.kmlMultiLineMode) return null;
      const validIds = new Set(this.kmlLineGroups.map((g) => g.id));
      const used = new Set();
      for (let i = 0; i < this.kmlRouteRows.length; i++) {
        const r = this.kmlRouteRows[i];
        const ids = this.normalizeRowGroupIdsArray(r);
        if (!ids.length) {
          return this.$t('crudLinhas.kmlAssistant.multiGroupIdRequired');
        }
        for (let j = 0; j < ids.length; j++) {
          if (!validIds.has(ids[j])) {
            return this.$t('crudLinhas.kmlAssistant.multiGroupIdRequired');
          }
          used.add(ids[j]);
        }
      }
      for (let i = 0; i < this.kmlFenceRows.length; i++) {
        const f = this.kmlFenceRows[i];
        const ids = this.normalizeRowGroupIdsArray(f);
        if (!ids.length) {
          return this.$t('crudLinhas.kmlAssistant.multiGroupIdRequired');
        }
        for (let j = 0; j < ids.length; j++) {
          if (!validIds.has(ids[j])) {
            return this.$t('crudLinhas.kmlAssistant.multiGroupIdRequired');
          }
          used.add(ids[j]);
        }
      }
      if (used.size < 2) {
        return this.$t('crudLinhas.kmlAssistant.multiDistinctGroups');
      }
      const routesByGroup = {};
      for (let i = 0; i < this.kmlRouteRows.length; i++) {
        const r = this.kmlRouteRows[i];
        const rowIds = this.normalizeRowGroupIdsArray(r);
        for (let j = 0; j < rowIds.length; j++) {
          const gid = rowIds[j];
          if (!routesByGroup[gid]) routesByGroup[gid] = [];
          routesByGroup[gid].push(r);
        }
      }
      const gids = Object.keys(routesByGroup);
      for (let i = 0; i < gids.length; i++) {
        const primaries = routesByGroup[gids[i]].filter((r) => this.isKmlPrimaryRota(r));
        if (primaries.length < 1) {
          return this.$t('crudLinhas.kmlAssistant.multiPrimaryPerGroup');
        }
      }
      const groupsWithRoutes = new Set(gids);
      for (let i = 0; i < this.kmlFenceRows.length; i++) {
        const fenceIds = this.normalizeRowGroupIdsArray(this.kmlFenceRows[i]);
        for (let j = 0; j < fenceIds.length; j++) {
          if (!groupsWithRoutes.has(fenceIds[j])) {
            return this.$t('crudLinhas.kmlAssistant.multiFencesWithoutRoutes');
          }
        }
      }
      return null;
    },
    getMultiLineDraftValidationError() {
      const ordered = this.kmlActiveGroupsOrdered;
      for (let i = 0; i < ordered.length; i++) {
        const gid = ordered[i];
        const d = this.kmlMultiLineDrafts[gid];
        if (!d || !d.editedItem) {
          return this.$t('crudLinhas.kmlAssistant.multiDraftValidation');
        }
        const ei = d.editedItem;
        if (!validador(ei.nome_linha, 1, 100, 2, 1)) {
          return this.$t('crudLinhas.validation.lineNameRequired');
        }
        if (!validador(ei.cod_linha, 1, 50, 0, 1)) {
          return this.$t('crudLinhas.validation.lineCodeRequired');
        }
        if (!ei.id_empresa) {
          return this.$t('crudLinhas.validation.companyRequired');
        }
        if (ei.lotacao_prevista && ei.lotacao_prevista <= 0) {
          return this.$t('crudLinhas.validation.mustBeGreaterThanZero');
        }
        if (d.isSublinha && !d.linhaPrincipal) {
          return this.$t('crudLinhas.validation.lineRequired');
        }
        if (d.velocidadeMaxima == null || String(d.velocidadeMaxima).length === 0) {
          return this.$t('crudLinhas.validation.maxSpeedRequired');
        }
        if (d.alarmeVelocidadeMaxima === true && Number(d.velocidadeMaxima) < 1) {
          return this.$t('crudLinhas.validation.maxSpeedMustBeGreaterThanZero');
        }
        if (d.limiteTempoParado !== null && d.limiteTempoParado !== undefined && d.limiteTempoParado !== '') {
          if (String(d.limiteTempoParado).length === 0) {
            return this.$t('crudLinhas.validation.stoppedTimeLimitRequired');
          }
          if (d.alarmeTempoParado === true && Number(d.limiteTempoParado) < 1) {
            return this.$t('crudLinhas.validation.stoppedTimeLimitMustBeGreaterThanZero');
          }
        }
        if (d.atualizaInicioViagem) {
          if (d.limiteTempoLargada == null || d.limiteTempoLargada === '' || String(d.limiteTempoLargada).length === 0) {
            return this.$t('crudLinhas.validation.departureTimeLimitRequired');
          }
          if (Number(d.limiteTempoLargada) < 1) {
            return this.$t('crudLinhas.validation.departureTimeLimitMustBeGreaterThanZero');
          }
        }
        const tol = d.toleranciaForaRota;
        if (tol != null && tol !== '' && Number(tol) < 0) {
          return this.$t('crudLinhas.validation.distanceMustBePositive');
        }
      }
      return null;
    },
    ensureKmlMultiDrafts() {
      for (let i = 0; i < this.kmlActiveGroupsOrdered.length; i++) {
        const gid = this.kmlActiveGroupsOrdered[i];
        if (!this.kmlMultiLineDrafts[gid]) {
          this.$set(this.kmlMultiLineDrafts, gid, this.createEmptyKmlLineDraft());
        } else {
          const d = this.kmlMultiLineDrafts[gid];
          if (d.assistantExistingLinha === undefined) this.$set(d, 'assistantExistingLinha', null);
          if (!Array.isArray(d.existingRotasLinhaByLinha)) this.$set(d, 'existingRotasLinhaByLinha', []);
          if (!Array.isArray(d.existingCercasLinhaByLinha)) this.$set(d, 'existingCercasLinhaByLinha', []);
        }
      }
    },
    createEmptyKmlLineDraft() {
      return {
        assistantExistingLinha: null,
        existingRotasLinhaByLinha: [],
        existingCercasLinhaByLinha: [],
        editedItem: { ...this.defaultItem },
        isSublinha: false,
        linhaPrincipal: null,
        isCircular: false,
        isAtiva: true,
        velocidadeMaxima: 0,
        limiteTempoParado: 0,
        alarmeTempoParado: false,
        alarmeForaRota: false,
        alarmeVelocidadeMaxima: false,
        limiteTempoLargada: 0,
        atualizaInicioViagem: false,
        toleranciaForaRota: null,
      };
    },
    multiDraftSublinhaChanged(gid) {
      const d = this.kmlMultiLineDrafts[gid];
      if (!d) return;
      if (!d.isSublinha) {
        d.linhaPrincipal = null;
        this.resetLinhaSelectRef('draftLinhaPrincipalSelect_' + gid);
        d.editedItem.id_linha_principal = null;
      }
    },
    multiDraftLinhaPrincipalChanged(gid) {
      const d = this.kmlMultiLineDrafts[gid];
      if (!d) return;
      if (d.linhaPrincipal) {
        d.editedItem.id_linha_principal = d.linhaPrincipal.id_linha;
      } else {
        d.editedItem.id_linha_principal = null;
      }
    },
    multiDraftVelocidadeMaximaChanged(gid) {
      const d = this.kmlMultiLineDrafts[gid];
      if (!d) return;
      if (d.alarmeVelocidadeMaxima === false) {
        d.velocidadeMaxima = 0;
      }
    },
    resetMultiDraftLineFormOnly(gid) {
      const d = this.kmlMultiLineDrafts[gid];
      if (!d) return;
      const fresh = this.createEmptyKmlLineDraft();
      this.resetLinhaSelectRef('draftAssistLinhaSelect_' + gid);
      this.resetLinhaSelectRef('draftLinhaPrincipalSelect_' + gid);
      this.$set(d, 'assistantExistingLinha', null);
      this.$set(d, 'existingRotasLinhaByLinha', []);
      this.$set(d, 'existingCercasLinhaByLinha', []);
      this.$set(d, 'editedItem', { ...this.defaultItem });
      this.$set(d, 'isSublinha', fresh.isSublinha);
      this.$set(d, 'linhaPrincipal', fresh.linhaPrincipal);
      this.$set(d, 'isCircular', fresh.isCircular);
      this.$set(d, 'isAtiva', fresh.isAtiva);
      this.$set(d, 'velocidadeMaxima', fresh.velocidadeMaxima);
      this.$set(d, 'limiteTempoParado', fresh.limiteTempoParado);
      this.$set(d, 'alarmeTempoParado', fresh.alarmeTempoParado);
      this.$set(d, 'alarmeForaRota', fresh.alarmeForaRota);
      this.$set(d, 'alarmeVelocidadeMaxima', fresh.alarmeVelocidadeMaxima);
      this.$set(d, 'limiteTempoLargada', fresh.limiteTempoLargada);
      this.$set(d, 'atualizaInicioViagem', fresh.atualizaInicioViagem);
      this.$set(d, 'toleranciaForaRota', fresh.toleranciaForaRota);
    },
    onMultiDraftAssistantExistingLinhaChange(gid, linha) {
      const d = this.kmlMultiLineDrafts[gid];
      if (!d) return;
      if (!linha) {
        this.resetMultiDraftLineFormOnly(gid);
        return;
      }
      this.applyAssistantLinhaSelectionToDraft(gid, linha);
    },
    applyAssistantLinhaSelectionToDraft(gid, linha) {
      if (!linha || linha.id_linha == null) return;
      const d = this.kmlMultiLineDrafts[gid];
      if (!d) return;
      d.assistantExistingLinha = linha;
      d.editedItem.id_linha = linha.id_linha;
      d.editedItem.id_empresa = linha.id_empresa;
      d.editedItem.nome_linha = linha.nome_linha;
      d.editedItem.descricao_linha = linha.descricao_linha != null ? linha.descricao_linha : null;
      d.editedItem.cod_linha = linha.cod_linha;
      d.editedItem.id_rota = linha.id_rota;
      d.editedItem.distancia_ida = linha.distancia_ida;
      d.editedItem.distancia_volta = linha.distancia_volta;
      d.editedItem.lotacao_prevista = linha.lotacao_prevista;
      d.editedItem.linha_intermunicipal = linha.linha_intermunicipal;
      d.editedItem.has_eta = linha.has_eta;
      d.editedItem.id_tipo_aberturas_viagens = linha.id_tipo_aberturas_viagens;
      d.editedItem.id_linha_principal = linha.id_linha_principal || null;
      d.editedItem.cod_integracao_prodata_tz = linha.cod_integracao_prodata_tz != null ? linha.cod_integracao_prodata_tz : null;
      d.isAtiva = Number(linha.ativa) === 1;
      d.isCircular = Number(linha.circular) === 1;
      d.velocidadeMaxima = linha.velocidade_maxima;
      d.alarmeVelocidadeMaxima = Number(linha.alarme_velocidade_maxima) === 1;
      d.alarmeForaRota = Number(linha.alarme_fora_rota) === 1;
      d.toleranciaForaRota = linha.tolerancia_fora_rota;
      d.limiteTempoParado = linha.limite_tempo_parado;
      d.alarmeTempoParado = Number(linha.alarme_tempo_parado) === 1;
      d.limiteTempoLargada = linha.tempo_maximo_largada;
      d.atualizaInicioViagem = Number(linha.atualiza_inicio_viagem) === 1;
      if (linha.id_linha_principal) {
        d.isSublinha = true;
        d.linhaPrincipal = { id_linha: linha.id_linha_principal };
      } else {
        d.isSublinha = false;
        d.linhaPrincipal = null;
        this.resetLinhaSelectRef('draftLinhaPrincipalSelect_' + gid);
      }
      this.fetchAssistLineRotasECercas(linha.id_linha)
        .then(({ rotas, cercas }) => {
          this.$set(d, 'existingRotasLinhaByLinha', rotas);
          this.$set(d, 'existingCercasLinhaByLinha', cercas);
        })
        .catch((e) => {
          this.$set(d, 'existingRotasLinhaByLinha', []);
          this.$set(d, 'existingCercasLinhaByLinha', []);
          EventBus.$emit('show-error', { error: e });
        });
    },
    fetchAssistLineRotasECercas(id_linha) {
      return Promise.all([
        apiController.request(apiController.endpoints.getRotaspadraoporlinha, { data: [id_linha], silent: true }),
        apiController.request(apiController.endpoints.getCercasporlinha, { data: [id_linha], silent: true }),
      ]).then(([rotasRes, cercasRes]) => ({
        rotas: Array.isArray(rotasRes.data) ? rotasRes.data : [],
        cercas: Array.isArray(cercasRes.data) ? cercasRes.data : [],
      }));
    },
    buildInsertLinhaPayloadFromDraft(draft) {
      const ei = { ...draft.editedItem };
      ei.ativa = draft.isAtiva ? 1 : 0;
      ei.circular = draft.isCircular ? 1 : 0;
      ei.tolerancia_fora_rota =
        draft.toleranciaForaRota != null && draft.toleranciaForaRota !== ''
          ? Number(draft.toleranciaForaRota)
          : 150;
      ei.alarme_fora_rota = draft.alarmeForaRota ? 1 : 0;
      ei.alarme_velocidade_maxima = draft.alarmeVelocidadeMaxima ? 1 : 0;
      ei.velocidade_maxima = Number(draft.velocidadeMaxima);
      ei.alarme_tempo_parado = draft.alarmeTempoParado ? 1 : 0;
      ei.atualiza_inicio_viagem = draft.atualizaInicioViagem ? 1 : 0;
      ei.limite_tempo_parado =
        draft.limiteTempoParado != null && draft.limiteTempoParado !== ''
          ? Number(draft.limiteTempoParado)
          : 0;
      ei.tempo_maximo_largada =
        draft.limiteTempoLargada != null && draft.limiteTempoLargada !== ''
          ? Number(draft.limiteTempoLargada)
          : 15;
      ei.distancia_ida =
        ei.distancia_ida != null && ei.distancia_ida !== '' ? Number(ei.distancia_ida) : 0;
      ei.distancia_volta =
        ei.distancia_volta != null && ei.distancia_volta !== '' ? Number(ei.distancia_volta) : 0;
      ei.id_tipo_aberturas_viagens = ei.id_tipo_aberturas_viagens || 1;
      ei.has_eta = ei.has_eta ? 1 : 0;
      ei.linha_intermunicipal = ei.linha_intermunicipal ? 1 : 0;
      ei.lotacao_prevista =
        ei.lotacao_prevista != null && ei.lotacao_prevista !== ''
          ? Number(ei.lotacao_prevista)
          : 0;
      if (draft.isSublinha && draft.linhaPrincipal) {
        ei.id_linha_principal = draft.linhaPrincipal.id_linha;
      } else {
        ei.id_linha_principal = null;
      }
      return ei;
    },
    close() {
      this.$emit('input', false);
    },
    populaRecursos() {
      if (!this.user) return;
      const onSuccess = (res) => {
        this.recursos = Array.isArray(res.data) ? res.data : [];
        this.populaEmpresasPermitidas();
      };
      const onError = () => {
        this.recursos = [];
      };
      if (this.user.is_superadmin === 1) {
        apiController.request(apiController.endpoints.getallRecursos, { silent: true, onSuccess, onError });
        return;
      }
      apiController.request(apiController.endpoints.getRecursosusuario, {
        params: { id: this.user.id_usuario },
        silent: true,
        onSuccess,
        onError,
      });
    },
    populaEmpresasPermitidas() {
      const recursosData = Array.isArray(this.recursos) ? this.recursos : [];
      this.empresasPermitidas = recursosData.filter((r) => r.id_empresa).map((rec) => rec.id_empresa);
      if (this.user && this.user.is_superadmin === 1) {
        this.empresasPermitidas = null;
      }
    },
    populaClientes() {
      apiController.request(apiController.endpoints.getallClientes, {
        silent: true,
        onSuccess: (res) => {
          this.clientes = Array.isArray(res.data) ? res.data : [];
        },
        onError: () => {
          this.clientes = [];
        },
      });
    },
    clientesPorEmpresaAssistente(idEmpresa) {
      if (idEmpresa == null) {
        return [];
      }
      return this.clientes.filter((c) => Number(c.id_empresa) === Number(idEmpresa));
    },
    onAssistenteEmpresaChanged() {
      if (this.editedItem.id_cliente != null) {
        const ok = this.clientes.some(
          (c) =>
            Number(c.id_cliente) === Number(this.editedItem.id_cliente) &&
            Number(c.id_empresa) === Number(this.editedItem.id_empresa)
        );
        if (!ok) {
          this.editedItem.id_cliente = null;
        }
      }
      // Mantém rotas do KML alinhadas à empresa da linha (evita 400 cliente×empresa no insertrota).
      if (!this.kmlMultiLineMode && this.editedItem.id_empresa != null && this.editedItem.id_empresa !== '') {
        (this.kmlRouteRows || []).forEach((row) => {
          if (row) this.$set(row, 'id_empresa', this.editedItem.id_empresa);
        });
      }
    },
    onAssistenteEmpresaChangedDraft(gid) {
      const d = this.kmlMultiLineDrafts[gid];
      if (!d || !d.editedItem) {
        return;
      }
      const ei = d.editedItem;
      if (ei.id_cliente != null) {
        const ok = this.clientes.some(
          (c) =>
            Number(c.id_cliente) === Number(ei.id_cliente) &&
            Number(c.id_empresa) === Number(ei.id_empresa)
        );
        if (!ok) {
          ei.id_cliente = null;
        }
      }
      if (ei.id_empresa != null && ei.id_empresa !== '') {
        (this.kmlRouteRows || [])
          .filter((r) => this.rowBelongsToKmlGroup(r, gid))
          .forEach((row) => {
            if (row) this.$set(row, 'id_empresa', ei.id_empresa);
          });
      }
    },
    /**
     * Empresa/cliente coerentes para insertrota/insertcerca.
     * Preferência: empresa da linha (draft) > empresa da linha do KML; cliente só se pertencer à empresa.
     */
    resolveKmlPersistEmpresaCliente(rowIdEmpresa, idClientePreferred, idEmpresaPreferred) {
      let id_empresa =
        idEmpresaPreferred != null && idEmpresaPreferred !== ''
          ? idEmpresaPreferred
          : rowIdEmpresa != null && rowIdEmpresa !== ''
            ? rowIdEmpresa
            : this.editedItem && this.editedItem.id_empresa != null && this.editedItem.id_empresa !== ''
              ? this.editedItem.id_empresa
              : null;
      let id_cliente =
        idClientePreferred != null && idClientePreferred !== '' ? idClientePreferred : null;
      if (id_cliente != null) {
        const cli = (this.clientes || []).find((c) => Number(c.id_cliente) === Number(id_cliente));
        if (!cli) {
          id_cliente = null;
        } else if (id_empresa == null || id_empresa === '') {
          id_empresa = cli.id_empresa;
        } else if (Number(cli.id_empresa) !== Number(id_empresa)) {
          id_cliente = null;
        }
      }
      return { id_empresa, id_cliente };
    },
    applySingleEmpresaAssistenteIfNeeded() {
      if (!this.value) {
        return;
      }
      if (!Array.isArray(this.empresas) || this.empresas.length !== 1) {
        return;
      }
      const id = this.empresas[0].id_empresa;
      if (this.editedItem.id_empresa == null || this.editedItem.id_empresa === '') {
        this.editedItem.id_empresa = id;
        this.onAssistenteEmpresaChanged();
      }
      if (Array.isArray(this.kmlRouteRows) && this.kmlRouteRows.length) {
        this.kmlRouteRows.forEach((row) => {
          if (!row || row.matchedExistingId) return;
          if (row.id_empresa == null || row.id_empresa === '') {
            this.$set(row, 'id_empresa', id);
          }
        });
      }
      if (this.kmlMultiLineDrafts && typeof this.kmlMultiLineDrafts === 'object') {
        Object.keys(this.kmlMultiLineDrafts).forEach((gid) => {
          const d = this.kmlMultiLineDrafts[gid];
          if (d && d.editedItem && (d.editedItem.id_empresa == null || d.editedItem.id_empresa === '')) {
            d.editedItem.id_empresa = id;
            this.onAssistenteEmpresaChangedDraft(gid);
          }
        });
      }
    },
    defaultEmpresaIdAssistente() {
      if (!Array.isArray(this.empresas) || !this.empresas.length) {
        return null;
      }
      if (this.empresas.length === 1) {
        return this.empresas[0].id_empresa;
      }
      if (this.editedItem && this.editedItem.id_empresa != null && this.editedItem.id_empresa !== '') {
        return this.editedItem.id_empresa;
      }
      return null;
    },
    populaEmpresas() {
      return apiController
        .request(apiController.endpoints.getallEmpresas, {
          silent: true,
          onSuccess: (res) => {
            this.empresas = Array.isArray(res.data) ? res.data : [];
            this.$nextTick(() => this.applySingleEmpresaAssistenteIfNeeded());
          },
          onError: () => {
            this.empresas = [];
          },
        })
        .catch(() => {
          this.empresas = [];
        });
    },
    async populaCercasCatalog() {
      // Prefer light payload (no shape/geojson); fall back to full select if recurso missing.
      try {
        const res = await apiController.request(apiController.endpoints.getCercasselectroteirizacao, {
          silent: true,
        });
        this.cercasCatalog = Array.isArray(res.data) ? res.data : [];
        return;
      } catch (_) {
        /* fall through */
      }
      try {
        const res = await apiController.request(apiController.endpoints.getCercasselect, {
          silent: true,
        });
        this.cercasCatalog = Array.isArray(res.data) ? res.data : [];
      } catch (_) {
        this.cercasCatalog = [];
      }
    },
    async populaRotasCatalog() {
      // Usa getallRotasativas existente e reduz no client (só extremos),
      // em chunks, para não travar a UI após o JSON pesado.
      try {
        const res = await apiController.request(apiController.endpoints.getallRotasativas, {
          silent: true,
        });
        const raw = Array.isArray(res.data) ? res.data : [];
        await yieldToMain();
        const out = [];
        for (let i = 0; i < raw.length; i += KML_MATCH_CHUNK_SIZE) {
          const slice = raw.slice(i, i + KML_MATCH_CHUNK_SIZE);
          out.push(...slimRotasCatalogForMatch(slice));
          if (i + KML_MATCH_CHUNK_SIZE < raw.length) {
            await yieldToMain();
          }
        }
        this.rotasCatalog = out;
      } catch (_) {
        this.rotasCatalog = [];
      }
    },
    revealMoreKmlRoutes() {
      this.kmlRouteListLimit = Math.min(
        this.kmlRouteRows.length,
        this.kmlRouteListLimit + KML_LIST_PAGE_SIZE,
      );
    },
    revealMoreKmlFences() {
      this.kmlFenceListLimit = Math.min(
        this.kmlFenceRows.length,
        this.kmlFenceListLimit + KML_LIST_PAGE_SIZE,
      );
    },
    async expandKmlListsGradually() {
      while (
        this.value &&
        (this.kmlRouteListLimit < this.kmlRouteRows.length ||
          this.kmlFenceListLimit < this.kmlFenceRows.length)
      ) {
        await yieldToMain();
        if (!this.value) return;
        if (this.kmlRouteListLimit < this.kmlRouteRows.length) {
          this.kmlRouteListLimit = Math.min(
            this.kmlRouteRows.length,
            this.kmlRouteListLimit + KML_LIST_PAGE_SIZE,
          );
        }
        if (this.kmlFenceListLimit < this.kmlFenceRows.length) {
          this.kmlFenceListLimit = Math.min(
            this.kmlFenceRows.length,
            this.kmlFenceListLimit + KML_LIST_PAGE_SIZE,
          );
        }
      }
    },
    /**
     * Registro completo da linha (getalllinhas paginado por id), com cache.
     * Os selects sao lazy e devolvem so id/codigo/nome, mas o assistente copia
     * ~20 campos da linha escolhida para o formulario.
     */
    async carregaDetalheLinha(idLinha) {
      if (idLinha == null || idLinha === '') return null;
      const chave = String(idLinha);
      if (this.linhaDetalhePorId[chave] !== undefined) {
        return this.linhaDetalhePorId[chave];
      }
      // Query montada fora da chamada: um literal aqui viraria allowlist em
      // apiEndpoints (generate:api-endpoints) e descartaria filter_id_linha.
      const consulta = Object.assign(
        { page: 1, rowsPerPage: 1, total: 'true', showInactives: 'true' },
        { filter_id_linha: chave }
      );
      try {
        const response = await apiController.request(apiController.endpoints.getallLinhas, {
          data: consulta,
          silent: true,
        });
        const items =
          response && response.data && Array.isArray(response.data.items)
            ? response.data.items
            : [];
        const linha = items.length ? items[0] : null;
        this.$set(this.linhaDetalhePorId, chave, linha);
        return linha;
      } catch (e) {
        return null;
      }
    },
    /** Zera um select lazy pelo nome do ref (os refs dos drafts vem em array). */
    resetLinhaSelectRef(refName) {
      const alvo = this.$refs[refName];
      const ref = Array.isArray(alvo) ? alvo[0] : alvo;
      if (ref && typeof ref.resetData === 'function') {
        ref.resetData();
      }
    },
    async onAssistantExistingLinhaInput(val) {
      if (val == null || val === '') {
        this.assistantExistingLinha = null;
        this.onAssistantExistingLinhaChange(null);
        return;
      }
      const linha = await this.carregaDetalheLinha(val);
      this.assistantExistingLinha = linha;
      this.onAssistantExistingLinhaChange(linha);
    },
    onLinhaPrincipalInput(val) {
      // So o id_linha e consumido daqui (editedItem.id_linha_principal).
      this.linhaPrincipal = val != null && val !== '' ? { id_linha: val } : null;
      this.linhaPrincipalChanged();
    },
    async onMultiDraftAssistantExistingLinhaInput(gid, val) {
      if (val == null || val === '') {
        this.onMultiDraftAssistantExistingLinhaChange(gid, null);
        return;
      }
      const linha = await this.carregaDetalheLinha(val);
      this.onMultiDraftAssistantExistingLinhaChange(gid, linha);
    },
    onMultiDraftLinhaPrincipalInput(gid, val) {
      const d = this.kmlMultiLineDrafts[gid];
      if (!d) return;
      this.$set(d, 'linhaPrincipal', val != null && val !== '' ? { id_linha: val } : null);
      this.multiDraftLinhaPrincipalChanged(gid);
    },
    populaSentidos() {
      return apiController
        .request(apiController.endpoints.getallSentidos, {
          silent: true,
          onSuccess: (res) => {
            const rows = Array.isArray(res.data) ? res.data : [];
            this.sentidos = rows.map((s) => ({
              ...s,
              descricao_sentido: translateDbField(s.translation_key, s.descricao_sentido),
            }));
            this.ensureDefaultRouteSentido();
          },
          onError: () => {
            this.sentidos = [];
          },
        })
        .catch(() => {
          this.sentidos = [];
        });
    },
    populaTiposRota() {
      return apiController
        .request(apiController.endpoints.getTiposrota, {
          silent: true,
          onSuccess: (res) => {
            const rows = Array.isArray(res.data) ? res.data : [];
            this.tiposRota = rows.map((t) => ({
              ...t,
              descricao_tipo_rota: translateDbField(t.translation_key, t.descricao_tipo_rota),
            }));
            this.ensureDefaultRouteTipo();
          },
          onError: () => {
            this.tiposRota = [];
          },
        })
        .catch(() => {
          this.tiposRota = [];
        });
    },
    populaTiposCerca() {
      return apiController
        .request(apiController.endpoints.getTiposcerca, {
          silent: true,
          onSuccess: (res) => {
            const rows = Array.isArray(res.data) ? res.data : [];
            this.tiposCerca = rows.map((t) => ({
              ...t,
              descricao_tipo_cerca: translateDbField(t.translation_key, t.descricao_tipo_cerca),
            }));
          },
          onError: () => {
            this.tiposCerca = [];
          },
        })
        .catch(() => {
          this.tiposCerca = [];
        });
    },
    ensureDefaultRouteTipo() {
      if (this.defaultRouteTipo != null || !this.tiposRota.length) return;
      const principal = this.tiposRota.find((t) => Number(t.id_tipo_rota) === 1);
      this.defaultRouteTipo = principal ? principal.id_tipo_rota : this.tiposRota[0].id_tipo_rota;
    },
    ensureDefaultRouteSentido() {
      if (this.defaultRouteSentido != null || !this.sentidos.length) return;
      const ida = this.sentidos.find((s) => Number(s.id_sentido) === 1);
      this.defaultRouteSentido = ida ? ida.id_sentido : this.sentidos[0].id_sentido;
    },
    applyDefaultRouteTipoToAll() {
      if (this.defaultRouteTipo == null || !this.kmlRouteRows.length) return;
      this.kmlRouteRows.forEach((row) => {
        this.$set(row, 'id_tipo_rota', this.defaultRouteTipo);
      });
    },
    applyDefaultRouteSentidoToAll() {
      if (this.defaultRouteSentido == null || !this.kmlRouteRows.length) return;
      this.kmlRouteRows.forEach((row) => {
        this.$set(row, 'id_sentido', this.defaultRouteSentido);
      });
    },
    populaTiposAberturasViagens() {
      apiController.request(apiController.endpoints.getallTiposaberturasviagens, {
        onSuccess: (res) => {
          this.tiposAberturasViagens = res.data || [];
        },
      });
    },
    populaTurnos() {
      apiController.request(apiController.endpoints.getallTurnos, {
        onSuccess: (res) => {
          this.turnosPassageiros = res.data || [];
        },
      });
    },
    populaUnidadesLinha(idLinha) {
      if (!idLinha) {
        this.unidadesLinhaIds = [];
        return;
      }
      apiController.request(apiController.endpoints.getallUnidadesativas, {
        silent: true,
        onSuccess: (response) => {
          const unidades = response.data || [];
          if (unidades.length === 0) {
            this.unidadesLinhaIds = [];
            return;
          }
          const requests = unidades.map((u) =>
            apiController
              .request(apiController.endpoints.getLinhasassociadas, {
                data: [u.id_unidade],
                silent: true,
              })
              .then((r) => ({ id_unidade: u.id_unidade, linhas: r.data || [] }))
          );
          Promise.all(requests).then((results) => {
            this.unidadesLinhaIds = results
              .filter((r) => r.linhas.some((l) => l.id_linha === idLinha))
              .map((r) => r.id_unidade);
          });
        },
        onError: (e) => {
          EventBus.$emit('show-error', { error: e });
          this.unidadesLinhaIds = [];
        },
      });
    },
    wizardBack() {
      if (this.wizardStep > 0) this.wizardStep--;
    },
    wizardNext() {
      if (this.wizardStep === 0) {
        if (this.kmlRouteRows.length === 0) {
          EventBus.$emit('show-alert', { mensagem: this.$t('crudLinhas.kmlAssistant.needRoutes'), tipo: 'error' });
          return;
        }
        if (!this.kmlRouteRowsValid) {
          EventBus.$emit('show-alert', { mensagem: this.$t('crudLinhas.kmlAssistant.fillAllRouteFields'), tipo: 'error' });
          return;
        }
        if (this.kmlMultiLineMode) {
          const multiErr = this.getKmlMultiLineValidationError();
          if (multiErr) {
            EventBus.$emit('show-alert', { mensagem: multiErr, tipo: 'error' });
            return;
          }
        } else if (!this.kmlRouteRows.some((r) => this.isKmlPrimaryRota(r))) {
          EventBus.$emit('show-alert', { mensagem: this.$t('crudLinhas.wizard.missingPrimaryRoute'), tipo: 'error' });
          return;
        }
        if (this.kmlFenceRows.length && !this.kmlFenceRowsValid) {
          EventBus.$emit('show-alert', { mensagem: this.$t('crudLinhas.kmlAssistant.fillAllFenceFields'), tipo: 'error' });
          return;
        }
        if (this.kmlMultiLineMode) {
          this.ensureKmlMultiDrafts();
          this.kmlMultiDraftTab = 0;
        }
      }
      if (this.wizardStep === 1) {
        if (this.kmlMultiLineMode) {
          const draftErr = this.getMultiLineDraftValidationError();
          if (draftErr) {
            EventBus.$emit('show-alert', { mensagem: draftErr, tipo: 'error' });
            return;
          }
        } else if (this.$refs.form) {
          this.$refs.form.validate();
          if (!this.formValidado) return;
        }
      }
      if (this.wizardStep < 2) this.wizardStep++;
    },
    velocidadeMaximaChanged() {
      if (this.alarmeVelocidadeMaxima === false) {
        this.velocidadeMaxima = 0;
      }
    },
    isSublinhaChanged() {
      if (!this.isSublinha) {
        this.linhaPrincipal = null;
        this.resetLinhaSelectRef('linhaPrincipalSelect');
        this.editedItem.id_linha_principal = null;
      }
    },
    linhaPrincipalChanged() {
      if (this.linhaPrincipal) {
        this.editedItem.id_linha_principal = this.linhaPrincipal.id_linha;
      } else {
        this.editedItem.id_linha_principal = null;
      }
    },
    parseKmlFile() {
      if (!this.kmlFile) return;
      const raw = Array.isArray(this.kmlFile) ? this.kmlFile[0] : this.kmlFile;
      if (!raw) return;
      // Garante Blob/File com nome .kml para o multer/validarupload aceitar o upload.
      const isBlob = typeof Blob !== 'undefined' && raw instanceof Blob;
      if (!isBlob) {
        EventBus.$emit('show-alert', {
          mensagem: this.$t('crudLinhas.kmlAssistant.selectKml'),
          tipo: 'error',
        });
        return;
      }
      const uploadName =
        raw.name && /\.kml$/i.test(String(raw.name))
          ? String(raw.name)
          : `${String(raw.name || 'upload').replace(/\.[^.]+$/, '') || 'upload'}.kml`;
      this.parsingKml = true;
      const run = async () => {
        try {
          await Promise.all([
            this.populaTiposRota().catch(() => {}),
            this.populaSentidos().catch(() => {}),
            this.populaTiposCerca().catch(() => {}),
            this.populaEmpresas().catch(() => {}),
            this.populaCercasCatalog().catch(() => {}),
            this.populaRotasCatalog().catch(() => {}),
          ]);
          if (this.kmlRouteCatalogsUnavailable) {
            EventBus.$emit('show-alert', {
              mensagem: this.$t('crudLinhas.kmlAssistant.routeCatalogsUnavailable'),
              tipo: 'error',
            });
            return;
          }
          if (this.kmlEmpresasUnavailable) {
            EventBus.$emit('show-alert', {
              mensagem: this.$t('crudLinhas.kmlAssistant.empresasUnavailable'),
              tipo: 'error',
            });
            return;
          }
          this.ensureDefaultRouteTipo();
          this.ensureDefaultRouteSentido();
          this.applySingleEmpresaAssistenteIfNeeded();
          const fd = new FormData();
          fd.append('kml', raw, uploadName);
          const res = await apiController.request(apiController.endpoints.parsekml_json, {
            data: fd,
            config: { timeout: 600000 },
            silent: true,
          });
          if (res.data.error || res.data.erro) {
            EventBus.$emit('show-alert', {
              mensagem:
                res.data.mensagem ||
                res.data.message ||
                res.data.msg ||
                this.$t('system.messages.error'),
              tipo: 'error',
            });
            return;
          }
          this.kmlRouteListLimit = KML_LIST_INITIAL_LIMIT;
          this.kmlFenceListLimit = KML_LIST_INITIAL_LIMIT;
          this.applyParsedKml(res.data);
          await this.$nextTick();
          await yieldToMain();
          if (!this.kmlRouteRows.length) {
            EventBus.$emit('show-alert', {
              mensagem: this.$t('crudLinhas.kmlAssistant.needRoutes'),
              tipo: 'error',
            });
            return;
          }
          await this.attachExistingCercaMatches();
          await yieldToMain();
          await this.attachExistingRotaMatches();
          await this.$nextTick();
          EventBus.$emit('show-alert', { mensagem: this.$t('crudLinhas.kmlAssistant.parseSuccess'), tipo: 'success' });
          if (Array.isArray(res.data.avisos) && res.data.avisos.length) {
            EventBus.$emit('show-alert', {
              mensagem: this.$t('crudLinhas.kmlAssistant.rerouteFailed'),
              tipo: 'warning',
            });
          }
          this.parsingKml = false;
          this.expandKmlListsGradually();
          try {
            await this.downloadParsedKmlZip(raw, uploadName);
          } catch (zipErr) {
            EventBus.$emit('show-alert', {
              mensagem:
                (zipErr && zipErr.message) || this.$t('crudLinhas.kmlAssistant.zipDownloadFailed'),
              tipo: 'warning',
            });
          }
        } catch (e) {
          const data = e && e.response && e.response.data;
          const apiMsg =
            data &&
            (data.mensagem || data.message || data.msg);
          EventBus.$emit('show-alert', {
            mensagem: apiMsg || (e && e.message) || this.$t('system.messages.error'),
            tipo: 'error',
          });
        } finally {
          this.parsingKml = false;
        }
      };
      run();
    },
    async downloadParsedKmlZip(kmlFile, uploadName) {
      const fd = new FormData();
      const name =
        uploadName ||
        (kmlFile && kmlFile.name && /\.kml$/i.test(String(kmlFile.name))
          ? String(kmlFile.name)
          : 'upload.kml');
      fd.append('kml', kmlFile, name);
      fd.append('download', '1');
      const res = await apiController.request(apiController.endpoints.parsekml, {
        data: fd,
        config: {
          responseType: 'blob',
          validateStatus: () => true,
          timeout: 600000,
        },
        silent: true,
      });
      if (res.status < 200 || res.status >= 300) {
        let detail = this.$t('crudLinhas.kmlAssistant.zipDownloadFailed');
        try {
          const text = await res.data.text();
          const j = JSON.parse(text);
          if (j.mensagem) {
            detail = j.mensagem;
          } else if (j.message) {
            detail = j.message;
          } else if (j.msg) {
            detail = j.msg;
          }
        } catch (_) {
          /* keep default message */
        }
        throw new Error(detail);
      }
      const ct = (res.headers['content-type'] || '').toLowerCase();
      if (ct.includes('application/json')) {
        let detail = this.$t('crudLinhas.kmlAssistant.zipDownloadFailed');
        try {
          const text = await res.data.text();
          const j = JSON.parse(text);
          if (j.msg) {
            detail = j.msg;
          } else if (j.mensagem) {
            detail = j.mensagem;
          }
        } catch (_) {
          /* keep default */
        }
        throw new Error(detail);
      }
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `merusparsedkml-${new Date().toISOString()}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    matchOneKmlFenceRow(row, cat, circularOnly, polygonOnly) {
      if (row.kind === 'existing') return row;
      let lat;
      let lng;
      if (row.kind === 'point') {
        const coords = row.feature && row.feature.geometry && row.feature.geometry.coordinates;
        if (!coords || coords.length < 2) {
          return { ...row, matchedExistingId: null, matchedFromCatalogPick: false };
        }
        lng = Number(coords[0]);
        lat = Number(coords[1]);
      } else {
        const rings = row.feature && row.feature.geometry && row.feature.geometry.coordinates;
        const ring = rings && rings[0];
        const c = ringCentroidLngLat(ring);
        if (!c) {
          return { ...row, matchedExistingId: null, matchedFromCatalogPick: false };
        }
        lat = c.lat;
        lng = c.lng;
      }
      if (!isFinite(lat) || !isFinite(lng)) {
        return { ...row, matchedExistingId: null, matchedFromCatalogPick: false };
      }
      // Prefer shape-compatible pool, then fall back (KML from roteirização may
      // approximate circular CD as Polygon).
      let hit = null;
      if (row.kind === 'point') {
        hit = findExistingCercaAt(lat, lng, circularOnly.length ? circularOnly : cat);
        if (!hit) {
          hit = findExistingCercaAt(lat, lng, cat);
        }
      } else {
        hit = findExistingCercaAt(lat, lng, polygonOnly.length ? polygonOnly : cat);
        if (!hit) {
          hit = findExistingCercaAt(lat, lng, circularOnly.length ? circularOnly : cat);
        }
        if (!hit) {
          hit = findExistingCercaAt(lat, lng, cat);
        }
      }
      if (!hit || hit.id_cerca == null) {
        return { ...row, matchedExistingId: null, matchedFromCatalogPick: false };
      }
      const matched = {
        ...row,
        matchedExistingId: hit.id_cerca,
        matchedFromCatalogPick: false,
        cod_cerca: hit.cod_cerca != null ? String(hit.cod_cerca) : row.cod_cerca,
        nome_cerca: hit.nome_cerca != null ? String(hit.nome_cerca) : row.nome_cerca,
      };
      // Polygon no KML que na verdade é CD/cerca circular cadastrada → trata como Point.
      if (
        row.kind === 'polygon' &&
        Number(hit.is_circular) === 1 &&
        hit.lat_centro != null &&
        hit.lng_centro != null
      ) {
        matched.kind = 'point';
        matched.raio_mts =
          Number(hit.raio_mts) > 0 ? Number(hit.raio_mts) : Number(row.raio_mts) > 0 ? Number(row.raio_mts) : 75;
        matched.feature = {
          type: 'Feature',
          properties: Object.assign({}, (row.feature && row.feature.properties) || {}, {
            name: hit.nome_cerca || (row.feature && row.feature.properties && row.feature.properties.name),
          }),
          geometry: {
            type: 'Point',
            coordinates: [Number(hit.lng_centro), Number(hit.lat_centro)],
          },
        };
      }
      return matched;
    },
    async attachExistingCercaMatches() {
      const cat = this.cercasCatalog;
      if (!Array.isArray(cat) || !cat.length) return;
      const { circularOnly, polygonOnly } = partitionCercasCatalogByShape(cat);
      const rows = this.kmlFenceRows;
      const out = new Array(rows.length);
      for (let i = 0; i < rows.length; i++) {
        out[i] = this.matchOneKmlFenceRow(rows[i], cat, circularOnly, polygonOnly);
        if ((i + 1) % KML_MATCH_CHUNK_SIZE === 0) {
          await yieldToMain();
        }
      }
      this.kmlFenceRows = out;
    },
    async attachExistingRotaMatches() {
      const cat = this.rotasCatalog;
      if (!Array.isArray(cat) || !cat.length) return;
      const rows = this.kmlRouteRows;
      const out = new Array(rows.length);
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const hit = findExistingRotaForFeature(row.feature, cat);
        if (!hit || hit.id_rota == null) {
          out[i] = { ...row, matchedExistingId: null, matchedFromCatalogPick: false };
        } else {
          out[i] = {
            ...row,
            matchedExistingId: hit.id_rota,
            matchedFromCatalogPick: false,
            cod_rota: hit.cod_rota != null ? String(hit.cod_rota) : row.cod_rota,
            nome_rota: hit.nome_rota != null ? String(hit.nome_rota) : row.nome_rota,
            id_empresa: hit.id_empresa != null ? hit.id_empresa : row.id_empresa,
          };
        }
        if ((i + 1) % KML_MATCH_CHUNK_SIZE === 0) {
          await yieldToMain();
        }
      }
      this.kmlRouteRows = out;
    },
    ensureFenceRaioDefault(row) {
      if (!row || row.kind !== 'point' || row.matchedExistingId) return;
      const r = row.raio_mts;
      if (r == null || r === '' || Number(r) <= 0) {
        this.$set(row, 'raio_mts', 75);
      }
    },
    normalizeCercaId(id) {
      if (id == null || id === '') return null;
      const n = Number(id);
      return Number.isFinite(n) ? n : id;
    },
    normalizeRotaId(id) {
      return this.normalizeCercaId(id);
    },
    onKmlFenceExistingCercaChange(idx, id_cerca) {
      const row = this.kmlFenceRows[idx];
      if (!row || row.fromKml) return;
      if (id_cerca == null || id_cerca === '') {
        this.unlinkKmlFenceMatch(idx);
        return;
      }
      const hit = this.cercasCatalog.find((c) => this.normalizeCercaId(c.id_cerca) === this.normalizeCercaId(id_cerca));
      if (!hit) return;
      const idNorm = this.normalizeCercaId(hit.id_cerca);
      this.$set(row, 'matchedExistingId', idNorm != null ? idNorm : hit.id_cerca);
      this.$set(row, 'matchedFromCatalogPick', true);
      this.$set(row, 'cod_cerca', hit.cod_cerca != null ? String(hit.cod_cerca) : '');
      this.$set(row, 'nome_cerca', hit.nome_cerca != null ? String(hit.nome_cerca) : '');
    },
    removeKmlFenceRow(idx) {
      if (idx < 0 || idx >= this.kmlFenceRows.length) return;
      if (this.kmlGeometryPreview.open && this.kmlGeometryPreview.scope === 'fence') {
        if (idx === this.kmlGeometryPreview.index) {
          this.closeKmlGeometryPreview();
        } else if (idx < this.kmlGeometryPreview.index) {
          this.kmlGeometryPreview.index -= 1;
        }
      }
      this.kmlFenceRows.splice(idx, 1);
    },
    removeAllKmlIdentifiedFences() {
      if (!this.kmlIdentifiedFenceCount) return;
      if (this.kmlGeometryPreview.open && this.kmlGeometryPreview.scope === 'fence') {
        this.closeKmlGeometryPreview();
      }
      this.kmlFenceRows = this.kmlFenceRows.filter((r) => !r.fromKml);
    },
    closeKmlGeometryPreview() {
      this.kmlGeometryPreview.open = false;
      this.kmlGeometryPreview.scope = null;
      this.kmlGeometryPreview.index = null;
      this.kmlGeometryPreview.title = '';
      this.kmlGeometryPreview.geojsonFeature = null;
      this.kmlGeometryPreview.geojsonStyle = {};
      this.kmlGeometryPreview.secondaryRoutesGeojson = null;
      this.kmlGeometryPreview.secondaryRoutesStyle = {};
      this.kmlGeometryPreview.fenceMapRouteHighlightIndex = null;
      this.kmlGeometryPreview.fenceMapHighlightedRouteFeature = null;
      this.kmlGeometryPreview.fenceMapHighlightedRouteStyle = {};
      this.kmlGeometryPreview.circle = null;
      this.kmlGeometryPreview.markerLatLng = null;
    },
    /** All KML route features as a FeatureCollection, optionally skipping one row index (focused route). */
    kmlRouteRowsFeatureCollection(excludeIndex = null) {
      const features = [];
      for (let i = 0; i < this.kmlRouteRows.length; i++) {
        if (excludeIndex != null && i === excludeIndex) continue;
        const row = this.kmlRouteRows[i];
        if (!this.kmlRouteRowHasMapGeometry(row)) continue;
        const f = row.feature;
        features.push({
          type: 'Feature',
          geometry: f.geometry,
          properties: {
            ...(f.properties && typeof f.properties === 'object' ? { ...f.properties } : {}),
            kmlRouteRowIndex: i,
          },
        });
      }
      if (!features.length) return null;
      return { type: 'FeatureCollection', features };
    },
    kmlRouteRowHasMapGeometry(row) {
      if (!row) return false;
      if (row.feature && row.feature.geometry) return true;
      if (row.matchedExistingId == null || row.matchedExistingId === '') return false;
      const r = this.kmlRouteResolvedCatalogEntry(row);
      // Match catalog is slim (endpoints only); treat start/end as enough for a stub marker.
      return !!(r && (r.geojson || r.shape || (r.start_lat != null && r.start_lng != null) || r._ends));
    },
    /** Base geometry/styles for one fence row (KML or catalog). */
    fenceRowToMapLayer(row) {
      if (!row || !this.kmlFenceRowHasMapGeometry(row)) return null;
      let geojsonFeature = null;
      let geojsonStyle = {};
      let circle = null;
      let markerLatLng = null;
      if (row.feature && row.feature.geometry) {
        const g = row.feature.geometry;
        if (row.kind === 'point' && g.type === 'Point' && Array.isArray(g.coordinates)) {
          const [lng, lat] = g.coordinates;
          const r = Number(row.raio_mts) > 0 ? Number(row.raio_mts) : 75;
          circle = { latLng: [lat, lng], radius: r };
        } else if (g.type === 'Polygon' || g.type === 'MultiPolygon') {
          geojsonFeature = row.feature;
          geojsonStyle = {
            color: '#2e7d32',
            weight: 2,
            fillColor: '#a5d6a7',
            fillOpacity: 0.35,
          };
        } else {
          geojsonFeature = row.feature;
          geojsonStyle = { color: '#6a1b9a', weight: 3, fillOpacity: 0.25 };
        }
      } else {
        const c = this.cercasCatalog.find(
          (x) => this.normalizeCercaId(x.id_cerca) === this.normalizeCercaId(row.matchedExistingId),
        );
        if (!c || c.lat_centro == null || c.lng_centro == null) return null;
        const lat = Number(c.lat_centro);
        const lng = Number(c.lng_centro);
        if (Number(c.is_circular) === 1 && Number(c.raio_mts) > 0) {
          circle = { latLng: [lat, lng], radius: Number(c.raio_mts) };
        } else {
          markerLatLng = [lat, lng];
        }
      }
      return { geojsonFeature, geojsonStyle, circle, markerLatLng };
    },
    mutedFenceGeojsonStyle(style) {
      if (style && style.fillColor === '#a5d6a7') {
        return { color: '#81c784', weight: 1, fillColor: '#dcedc8', fillOpacity: 0.2 };
      }
      return { color: '#b39ddb', weight: 2, fillOpacity: 0.12 };
    },
    onKmlMapOverlayInput(open) {
      if (!open) this.closeKmlGeometryPreview();
    },
    onKmlMapSelectRoute(idx) {
      const i = Number(idx);
      if (!Number.isFinite(i) || i < 0 || i >= this.kmlRouteRows.length) return;
      if (i + 1 > this.kmlRouteListLimit) {
        this.kmlRouteListLimit = Math.min(this.kmlRouteRows.length, i + 1);
      }
      this.$nextTick(() => {
        const el =
          typeof document !== 'undefined' && document.getElementById(`kml-route-entry-${i}`);
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        this.$nextTick(() => {
          if (!el || !el.querySelector) return;
          const focusable = el.querySelector(
            '.v-text-field input, .v-select input, .v-autocomplete input, textarea',
          );
          if (focusable && typeof focusable.focus === 'function') {
            try {
              focusable.focus({ preventScroll: true });
            } catch (e) {
              focusable.focus();
            }
          }
        });
      });
    },
    onKmlMapSelectFence(idx) {
      const i = Number(idx);
      if (!Number.isFinite(i) || i < 0 || i >= this.kmlFenceRows.length) return;
      if (i + 1 > this.kmlFenceListLimit) {
        this.kmlFenceListLimit = Math.min(this.kmlFenceRows.length, i + 1);
      }
      this.$nextTick(() => {
        const el =
          typeof document !== 'undefined' && document.getElementById(`kml-fence-entry-${i}`);
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        this.$nextTick(() => {
          if (!el || !el.querySelector) return;
          const focusable = el.querySelector(
            '.v-text-field input, .v-select input, .v-autocomplete input, textarea',
          );
          if (focusable && typeof focusable.focus === 'function') {
            try {
              focusable.focus({ preventScroll: true });
            } catch (e) {
              focusable.focus();
            }
          }
        });
      });
    },
    /** First vertex as [lat, lng] for Leaflet (GeoJSON positions are [lng, lat]). */
    kmlFirstLatLngFromRouteFeature(feature) {
      const g = feature && feature.geometry;
      if (!g || !g.coordinates) return null;
      let c = null;
      if (g.type === 'LineString' && Array.isArray(g.coordinates) && g.coordinates.length) {
        c = g.coordinates[0];
      } else if (
        g.type === 'MultiLineString' &&
        Array.isArray(g.coordinates) &&
        g.coordinates.length &&
        Array.isArray(g.coordinates[0]) &&
        g.coordinates[0].length
      ) {
        c = g.coordinates[0][0];
      }
      if (!c || c.length < 2) return null;
      const lat = Number(c[1]);
      const lng = Number(c[0]);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return [lat, lng];
    },
    /** [lat, lng] at geometric center of fence preview (point, circle, polygon centroid, or bounds). */
    kmlCenterLatLngFromFencePreview({ geojsonFeature, circle, markerLatLng }) {
      if (circle && Array.isArray(circle.latLng) && circle.latLng.length >= 2) {
        const [lat, lng] = circle.latLng;
        if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
      }
      if (markerLatLng && markerLatLng.length >= 2) {
        const [lat, lng] = markerLatLng;
        if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
      }
      if (geojsonFeature && geojsonFeature.geometry) {
        const g = geojsonFeature.geometry;
        if (g.type === 'Polygon' && g.coordinates && g.coordinates[0]) {
          const cen = ringCentroidLngLat(g.coordinates[0]);
          if (cen && Number.isFinite(cen.lat) && Number.isFinite(cen.lng)) return [cen.lat, cen.lng];
        }
        if (g.type === 'MultiPolygon' && g.coordinates && g.coordinates[0] && g.coordinates[0][0]) {
          const cen = ringCentroidLngLat(g.coordinates[0][0]);
          if (cen && Number.isFinite(cen.lat) && Number.isFinite(cen.lng)) return [cen.lat, cen.lng];
        }
        if (g.type === 'Point' && Array.isArray(g.coordinates) && g.coordinates.length >= 2) {
          const lng = Number(g.coordinates[0]);
          const lat = Number(g.coordinates[1]);
          if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
        }
        try {
          const layer = L.geoJSON(geojsonFeature);
          const b = layer.getBounds();
          if (b.isValid()) {
            const c = b.getCenter();
            return [c.lat, c.lng];
          }
        } catch (e) {
          /* ignore */
        }
      }
      return null;
    },
    kmlFenceRowHasMapGeometry(row) {
      if (!row) return false;
      if (row.feature && row.feature.geometry) return true;
      if (row.matchedExistingId == null || row.matchedExistingId === '') return false;
      const c = this.cercasCatalog.find(
        (x) => this.normalizeCercaId(x.id_cerca) === this.normalizeCercaId(row.matchedExistingId),
      );
      return !!(c && c.lat_centro != null && c.lng_centro != null);
    },
    kmlFenceResolvedCatalogEntry(row) {
      if (!row || row.matchedExistingId == null || row.matchedExistingId === '') return null;
      return (
        this.cercasCatalog.find(
          (x) => this.normalizeCercaId(x.id_cerca) === this.normalizeCercaId(row.matchedExistingId),
        ) || null
      );
    },
    kmlRouteResolvedCatalogEntry(row) {
      if (!row || row.matchedExistingId == null || row.matchedExistingId === '') return null;
      return (
        this.rotasCatalog.find(
          (x) => this.normalizeRotaId(x.id_rota) === this.normalizeRotaId(row.matchedExistingId),
        ) || null
      );
    },
    kmlRouteExistingDisplayNome(row) {
      const t = row.nome_rota != null && String(row.nome_rota).trim();
      if (t) return t;
      const r = this.kmlRouteResolvedCatalogEntry(row);
      if (r && r.nome_rota != null && String(r.nome_rota).trim()) return String(r.nome_rota).trim();
      return '';
    },
    kmlRouteExistingDisplayCod(row) {
      const t = row.cod_rota != null && String(row.cod_rota).trim();
      if (t) return t;
      const r = this.kmlRouteResolvedCatalogEntry(row);
      if (r && r.cod_rota != null && String(r.cod_rota).trim()) return String(r.cod_rota).trim();
      return '';
    },
    kmlRouteNomeFieldDisplay(row) {
      if (!row.matchedExistingId) return row.nome_rota;
      return this.kmlRouteExistingDisplayNome(row) || row.nome_rota || '';
    },
    kmlRouteCodFieldDisplay(row) {
      if (!row.matchedExistingId) return row.cod_rota;
      return this.kmlRouteExistingDisplayCod(row) || row.cod_rota || '';
    },
    kmlFenceExistingDisplayNome(row) {
      const t = row.nome_cerca != null && String(row.nome_cerca).trim();
      if (t) return t;
      const c = this.kmlFenceResolvedCatalogEntry(row);
      if (c && c.nome_cerca != null && String(c.nome_cerca).trim()) return String(c.nome_cerca).trim();
      return '';
    },
    kmlFenceExistingDisplayCod(row) {
      const t = row.cod_cerca != null && String(row.cod_cerca).trim();
      if (t) return t;
      const c = this.kmlFenceResolvedCatalogEntry(row);
      if (c && c.cod_cerca != null && String(c.cod_cerca).trim()) return String(c.cod_cerca).trim();
      return '';
    },
    kmlFenceExistingCodNomeLine(row) {
      const cod = this.kmlFenceExistingDisplayCod(row);
      const nome = this.kmlFenceExistingDisplayNome(row);
      if (!cod && !nome) return '';
      return cod ? `${cod} — ${nome}` : nome;
    },
    kmlFenceNomeFieldDisplay(row) {
      if (!row.matchedExistingId) return row.nome_cerca;
      return this.kmlFenceExistingDisplayNome(row) || row.nome_cerca || '';
    },
    kmlFenceCodFieldDisplay(row) {
      if (!row.matchedExistingId) return row.cod_cerca;
      return this.kmlFenceExistingDisplayCod(row) || row.cod_cerca || '';
    },
    kmlFenceMapPreviewTitleName(row) {
      if (row.matchedExistingId != null && row.matchedExistingId !== '') {
        const line = this.kmlFenceExistingCodNomeLine(row);
        if (line) return line;
      }
      const nome = row.nome_cerca != null && String(row.nome_cerca).trim();
      if (nome) return nome;
      return row.kmlLabel || '—';
    },
    /** Plain-text lines for map hover (Leaflet tooltip). */
    kmlFenceMapStopTooltipText(row) {
      const lines = [];
      const title = this.kmlFenceMapPreviewTitleName(row);
      if (title) lines.push(title);
      const cod = this.kmlFenceCodFieldDisplay(row);
      const codStr = cod != null && String(cod).trim();
      if (codStr && (!title || !String(title).includes(codStr))) {
        lines.push(this.$t('crudLinhas.kmlAssistant.stopMapTooltipCode', { cod: codStr }));
      }
      const ord = row.ordem_parada;
      if (ord != null && ord !== '') {
        const on = Number(ord);
        if (Number.isFinite(on)) {
          lines.push(this.$t('crudLinhas.kmlAssistant.stopMapTooltipOrder', { n: on }));
        }
      }
      const tipo = this.tiposCerca.find((t) => t.id_tipo_cerca === row.id_tipo_cerca);
      if (tipo && tipo.descricao_tipo_cerca) {
        lines.push(this.$t('crudLinhas.kmlAssistant.stopMapTooltipType', { label: tipo.descricao_tipo_cerca }));
      }
      const sent = this.sentidos.find((s) => s.id_sentido === row.id_sentido);
      if (sent && sent.descricao_sentido) {
        lines.push(this.$t('crudLinhas.kmlAssistant.stopMapTooltipDirection', { label: sent.descricao_sentido }));
      }
      let radiusM = Number(row.raio_mts);
      if (!Number.isFinite(radiusM) || radiusM <= 0) {
        if (row.matchedExistingId != null && row.matchedExistingId !== '') {
          const c = this.cercasCatalog.find(
            (x) => this.normalizeCercaId(x.id_cerca) === this.normalizeCercaId(row.matchedExistingId),
          );
          if (c && Number(c.is_circular) === 1) {
            radiusM = Number(c.raio_mts);
          }
        }
      }
      if (Number.isFinite(radiusM) && radiusM > 0) {
        lines.push(this.$t('crudLinhas.kmlAssistant.stopMapTooltipRadius', { n: radiusM }));
      }
      const tch = row.tempo_chegada_minutos;
      if (tch != null && tch !== '') {
        const tm = Number(tch);
        if (Number.isFinite(tm)) {
          lines.push(this.$t('crudLinhas.kmlAssistant.stopMapTooltipArrival', { n: tm }));
        }
      }
      const kl = row.kmlLabel != null && String(row.kmlLabel).trim();
      if (kl && kl !== title) {
        lines.push(this.$t('crudLinhas.kmlAssistant.stopMapTooltipKmlLabel', { label: kl }));
      }
      return lines.join('\n');
    },
    applyKmlRoutePreviewLayers(focusIdx) {
      const row = this.kmlRouteRows[focusIdx];
      if (!row || !this.kmlRouteRowHasMapGeometry(row)) return;
      this.kmlGeometryPreview.title = this.$t('crudLinhas.kmlAssistant.mapPreviewRoute', {
        name: row.nome_rota || row.kmlLabel || '—',
      });
      this.kmlGeometryPreview.secondaryRoutesGeojson = this.kmlRouteRowsFeatureCollection(focusIdx);
      this.kmlGeometryPreview.secondaryRoutesStyle = {
        color: '#64b5f6',
        weight: 3,
        opacity: 0.72,
        className: 'kml-map-route-layer',
      };
      this.kmlGeometryPreview.geojsonFeature = row.feature;
      this.kmlGeometryPreview.geojsonStyle = {
        color: '#1976d2',
        weight: 5,
        opacity: 0.95,
        className: 'kml-map-route-layer',
      };
      this.kmlGeometryPreview.fenceMapHighlightedRouteFeature = null;
      this.kmlGeometryPreview.fenceMapHighlightedRouteStyle = {};
      this.kmlGeometryPreview.circle = null;
      this.kmlGeometryPreview.markerLatLng = null;
      const firstLL = this.kmlFirstLatLngFromRouteFeature(row.feature);
      if (firstLL) {
        this.kmlGeometryPreview.center = firstLL;
      }
    },
    /** Background + highlighted KML routes on the fence/stops map (stop geometry is set separately). */
    applyFenceMapRouteHighlightLayers() {
      const focus = this.kmlGeometryPreview.fenceMapRouteHighlightIndex;
      const row = focus != null ? this.kmlRouteRows[focus] : null;
      if (!row || !this.kmlRouteRowHasMapGeometry(row)) {
        this.kmlGeometryPreview.fenceMapHighlightedRouteFeature = null;
        this.kmlGeometryPreview.fenceMapHighlightedRouteStyle = {};
        this.kmlGeometryPreview.secondaryRoutesGeojson = this.kmlRouteRowsFeatureCollection(null);
        this.kmlGeometryPreview.secondaryRoutesStyle = {
          color: '#1565c0',
          weight: 3,
          opacity: 0.5,
          className: 'kml-map-route-layer',
        };
        return;
      }
      this.kmlGeometryPreview.secondaryRoutesGeojson = this.kmlRouteRowsFeatureCollection(focus);
      this.kmlGeometryPreview.secondaryRoutesStyle = {
        color: '#64b5f6',
        weight: 3,
        opacity: 0.72,
        className: 'kml-map-route-layer',
      };
      this.kmlGeometryPreview.fenceMapHighlightedRouteFeature = row.feature;
      this.kmlGeometryPreview.fenceMapHighlightedRouteStyle = {
        color: '#1976d2',
        weight: 5,
        opacity: 0.95,
        className: 'kml-map-route-layer',
      };
    },
    onFenceMapRouteHighlightChange(newIdx) {
      if (newIdx == null || newIdx === '') return;
      const idx = Number(newIdx);
      if (!Number.isFinite(idx) || idx < 0 || idx >= this.kmlRouteRows.length) return;
      if (idx === this.kmlGeometryPreview.fenceMapRouteHighlightIndex) return;
      const row = this.kmlRouteRows[idx];
      if (!row || !this.kmlRouteRowHasMapGeometry(row)) {
        EventBus.$emit('show-alert', {
          mensagem: this.$t('crudLinhas.kmlAssistant.noMapGeometry'),
          tipo: 'warning',
        });
        return;
      }
      this.kmlGeometryPreview.mapKey += 1;
      this.kmlGeometryPreview.fenceMapRouteHighlightIndex = idx;
      this.applyFenceMapRouteHighlightLayers();
    },
    openKmlRouteMapPreview(idx) {
      const row = this.kmlRouteRows[idx];
      if (!this.kmlRouteRowHasMapGeometry(row)) {
        EventBus.$emit('show-alert', {
          mensagem: this.$t('crudLinhas.kmlAssistant.noMapGeometry'),
          tipo: 'warning',
        });
        return;
      }
      if (
        this.kmlGeometryPreview.open &&
        this.kmlGeometryPreview.scope === 'route' &&
        this.kmlGeometryPreview.index === idx
      ) {
        this.closeKmlGeometryPreview();
        return;
      }
      this.kmlGeometryPreview.mapKey += 1;
      this.kmlGeometryPreview.scope = 'route';
      this.kmlGeometryPreview.index = idx;
      this.applyKmlRoutePreviewLayers(idx);
      this.kmlGeometryPreview.open = true;
    },
    openKmlFenceMapPreview(idx) {
      const row = this.kmlFenceRows[idx];
      if (!this.kmlFenceRowHasMapGeometry(row)) {
        EventBus.$emit('show-alert', {
          mensagem: this.$t('crudLinhas.kmlAssistant.noMapGeometry'),
          tipo: 'warning',
        });
        return;
      }
      if (
        this.kmlGeometryPreview.open &&
        this.kmlGeometryPreview.scope === 'fence' &&
        this.kmlGeometryPreview.index === idx
      ) {
        this.closeKmlGeometryPreview();
        return;
      }

      const layerGeom = this.fenceRowToMapLayer(row);
      if (!layerGeom) {
        EventBus.$emit('show-alert', {
          mensagem: this.$t('crudLinhas.kmlAssistant.noMapGeometry'),
          tipo: 'warning',
        });
        return;
      }

      this.kmlGeometryPreview.geojsonFeature = null;
      this.kmlGeometryPreview.geojsonStyle = {};
      this.kmlGeometryPreview.circle = null;
      this.kmlGeometryPreview.markerLatLng = null;

      this.kmlGeometryPreview.mapKey += 1;
      this.kmlGeometryPreview.scope = 'fence';
      this.kmlGeometryPreview.index = idx;
      this.kmlGeometryPreview.title = this.$t('crudLinhas.kmlAssistant.mapPreviewFence', {
        name: this.kmlFenceMapPreviewTitleName(row),
      });
      let defaultRouteFocus = null;
      for (let i = 0; i < this.kmlRouteRows.length; i++) {
        if (this.kmlRouteRowHasMapGeometry(this.kmlRouteRows[i])) {
          defaultRouteFocus = i;
          break;
        }
      }
      this.kmlGeometryPreview.fenceMapRouteHighlightIndex = defaultRouteFocus;
      this.applyFenceMapRouteHighlightLayers();
      const fenceCenter = this.kmlCenterLatLngFromFencePreview({
        geojsonFeature: layerGeom.geojsonFeature,
        circle: layerGeom.circle,
        markerLatLng: layerGeom.markerLatLng,
      });
      if (fenceCenter) {
        this.kmlGeometryPreview.center = fenceCenter;
      }
      this.kmlGeometryPreview.open = true;
    },
    applyDefaultFenceSentidoToSelected() {
      if (this.defaultFenceSentido == null || !this.kmlFenceSelectedCount) return;
      this.kmlFenceRows.forEach((row) => {
        if (!row.selected) return;
        this.$set(row, 'id_sentido', this.defaultFenceSentido);
      });
    },
    applyDefaultFenceTipoToSelected() {
      if (this.defaultFenceTipo == null || !this.kmlFenceSelectedCount) return;
      this.kmlFenceRows.forEach((row) => {
        if (!row.selected) return;
        this.$set(row, 'id_tipo_cerca', this.defaultFenceTipo);
      });
    },
    applyDefaultsToFenceRow(row) {
      if (!row) return;
      if (this.defaultFenceSentido != null) {
        this.$set(row, 'id_sentido', this.defaultFenceSentido);
      }
      if (this.defaultFenceTipo != null) {
        this.$set(row, 'id_tipo_cerca', this.defaultFenceTipo);
      }
    },
    onKmlFenceSelectedChange(idx, selected) {
      const row = this.kmlFenceRows[idx];
      if (!row) return;
      this.$set(row, 'selected', !!selected);
      if (selected) this.applyDefaultsToFenceRow(row);
    },
    toggleSelectAllKmlFences(val) {
      const selected = !!val;
      this.kmlFenceRows.forEach((row) => {
        this.$set(row, 'selected', selected);
        if (selected) this.applyDefaultsToFenceRow(row);
      });
    },
    clearKmlFenceSelection() {
      this.kmlFenceRows.forEach((row) => {
        this.$set(row, 'selected', false);
      });
    },
    addKmlFenceRowFromCatalog() {
      const defaultIds =
        this.kmlMultiLineMode && this.kmlLineGroups.length
          ? [this.kmlLineGroups[0].id]
          : [KML_SINGLE_GROUP_ID];
      let nextOrd = Number(this.defaultOrdemStart) || 1;
      if (this.kmlFenceRows.length) {
        const maxO = this.kmlFenceRows.reduce((m, f) => {
          const o = Number(f.ordem_parada);
          return Number.isFinite(o) && o > m ? o : m;
        }, 0);
        nextOrd = maxO + 1;
      }
      this.kmlFenceRows.push({
        kind: 'existing',
        feature: null,
        fromKml: false,
        kmlLabel: this.$t('crudLinhas.kmlAssistant.existingFenceRowLabel'),
        matchedExistingId: null,
        matchedFromCatalogPick: false,
        cod_cerca: '',
        nome_cerca: '',
        id_tipo_cerca: this.defaultFenceTipo,
        id_sentido: this.defaultFenceSentido,
        ordem_parada: nextOrd,
        tempo_chegada_minutos: null,
        raio_mts: null,
        kmlGroupIds: [...defaultIds],
        selected: false,
      });
    },
    unlinkKmlFenceMatch(idx) {
      const row = this.kmlFenceRows[idx];
      if (!row || row.matchedExistingId == null || row.matchedExistingId === '') return;
      const label = row.kmlLabel || 'cerca';
      const fresh = {
        ...row,
        matchedExistingId: null,
        matchedFromCatalogPick: false,
        cod_cerca: '',
        nome_cerca: String(label).replace(/-/g, ' '),
      };
      this.$set(this.kmlFenceRows, idx, fresh);
    },
    unlinkKmlRouteMatch(idx) {
      const row = this.kmlRouteRows[idx];
      if (!row || row.matchedExistingId == null || row.matchedExistingId === '') return;
      const label = row.kmlLabel || 'route';
      const fresh = {
        ...row,
        matchedExistingId: null,
        matchedFromCatalogPick: false,
        cod_rota: '',
        nome_rota: String(label).replace(/-/g, ' '),
        id_empresa: null,
      };
      this.$set(this.kmlRouteRows, idx, fresh);
    },
    applyParsedKml(data) {
      const defaultIds =
        this.kmlMultiLineMode && this.kmlLineGroups.length
          ? [this.kmlLineGroups[0].id]
          : [KML_SINGLE_GROUP_ID];
      const lines = data.lineStrings || [];
      this.kmlRouteRows = lines.map((feat) => {
        const props = (feat && feat.properties) || {};
        const label = props._kmlLabel || 'route';
        const nomeFromKml =
          props.nome_rota != null && String(props.nome_rota).trim()
            ? String(props.nome_rota).trim()
            : props.name != null && String(props.name).trim()
              ? String(props.name).trim()
              : label.replace(/-/g, ' ');
        const codFromKml =
          props.cod_rota != null && String(props.cod_rota).trim()
            ? String(props.cod_rota).trim()
            : props.codigo != null && String(props.codigo).trim()
              ? String(props.codigo).trim()
              : '';
        return {
          feature: feat,
          kmlLabel: label,
          cod_rota: codFromKml,
          nome_rota: nomeFromKml,
          id_empresa: this.defaultEmpresaIdAssistente(),
          id_tipo_rota: this.defaultRouteTipo,
          id_sentido: this.defaultRouteSentido,
          matchedExistingId: null,
          matchedFromCatalogPick: false,
          kmlGroupIds: [...defaultIds],
        };
      });
      const polys = data.polygons || [];
      const pts = data.points || [];
      let ordem = this.defaultOrdemStart || 1;
      const usedFenceNomes = new Set();
      const usedFenceCods = new Set();
      const uniqueFenceNome = (base) => {
        let name = String(base || 'cerca').trim() || 'cerca';
        if (!usedFenceNomes.has(name.toLowerCase())) {
          usedFenceNomes.add(name.toLowerCase());
          return name;
        }
        let i = 2;
        let candidate = `${name} ${i}`;
        while (usedFenceNomes.has(candidate.toLowerCase())) {
          i += 1;
          candidate = `${name} ${i}`;
        }
        usedFenceNomes.add(candidate.toLowerCase());
        return candidate;
      };
      const uniqueFenceCod = (label, kind, ordemParada) => {
        const prefix = kind === 'point' ? 'PT' : 'PG';
        const fromLabel = sanitizeCercaCodForInsert(`${prefix}_${String(label || '').replace(/-/g, '_')}`);
        let cod =
          fromLabel ||
          sanitizeCercaCodForInsert(`${prefix}${ordemParada}`) ||
          `${prefix}${ordemParada}`;
        if (!usedFenceCods.has(cod.toLowerCase())) {
          usedFenceCods.add(cod.toLowerCase());
          return cod;
        }
        let i = 2;
        let candidate = sanitizeCercaCodForInsert(`${cod}_${i}`) || `${prefix}${ordemParada}_${i}`;
        while (usedFenceCods.has(candidate.toLowerCase())) {
          i += 1;
          candidate = sanitizeCercaCodForInsert(`${cod}_${i}`) || `${prefix}${ordemParada}_${i}`;
        }
        usedFenceCods.add(candidate.toLowerCase());
        return candidate;
      };
      const fences = [];
      polys.forEach((feat) => {
        const label = (feat.properties && feat.properties._kmlLabel) || 'polygon';
        const ordemParada = ordem++;
        const nomeBase = label.replace(/-/g, ' ');
        fences.push({
          kind: 'polygon',
          feature: feat,
          kmlLabel: label,
          fromKml: true,
          matchedFromCatalogPick: false,
          cod_cerca: uniqueFenceCod(label, 'polygon', ordemParada),
          nome_cerca: uniqueFenceNome(nomeBase),
          id_tipo_cerca: this.defaultFenceTipo,
          id_sentido: this.defaultFenceSentido,
          ordem_parada: ordemParada,
          tempo_chegada_minutos: null,
          raio_mts: 75,
          kmlGroupIds: [...defaultIds],
          selected: false,
        });
      });
      pts.forEach((feat) => {
        const label = (feat.properties && feat.properties._kmlLabel) || 'point';
        const ordemParada = ordem++;
        const nomeBase = label.replace(/-/g, ' ');
        fences.push({
          kind: 'point',
          feature: feat,
          kmlLabel: label,
          fromKml: true,
          matchedFromCatalogPick: false,
          cod_cerca: uniqueFenceCod(label, 'point', ordemParada),
          nome_cerca: uniqueFenceNome(nomeBase),
          id_tipo_cerca: this.defaultFenceTipo,
          id_sentido: this.defaultFenceSentido,
          ordem_parada: ordemParada,
          tempo_chegada_minutos: null,
          raio_mts: 75,
          kmlGroupIds: [...defaultIds],
          selected: false,
        });
      });
      this.kmlFenceRows = fences.map((f) => {
        if (f.kind === 'point' && (f.raio_mts == null || f.raio_mts === '' || Number(f.raio_mts) <= 0)) {
          return { ...f, raio_mts: 75 };
        }
        return f;
      });
    },
    syncIdRotaFromRotasLinhaInto(target) {
      if (!target || !this.rotasLinha || this.rotasLinha.length === 0) return;
      const primary = this.rotasLinha.find((r) => Number(r.id_tipo_rota) === 1);
      const pick = primary || this.rotasLinha[0];
      if (!pick || !pick.id_rota) return;
      target.id_rota = pick.id_rota;
      target.nome_rota = pick.nome_rota;
    },
    syncIdRotaFromRotasLinha() {
      this.syncIdRotaFromRotasLinhaInto(this.editedItem);
    },
    resetAssistantLineFormOnly() {
      this.editedItem = { ...this.defaultItem };
      this.isSublinha = false;
      this.linhaPrincipal = null;
      this.resetLinhaSelectRef('linhaPrincipalSelect');
      this.isAtiva = true;
      this.isCircular = false;
      this.velocidadeMaxima = 0;
      this.limiteTempoParado = 0;
      this.alarmeTempoParado = false;
      this.alarmeForaRota = false;
      this.alarmeVelocidadeMaxima = false;
      this.atualizaInicioViagem = false;
      this.limiteTempoLargada = 0;
      this.toleranciaForaRota = null;
    },
    onAssistantExistingLinhaChange(linha) {
      if (!linha) {
        this.resetAssistantLineFormOnly();
        this.existingRotasLinhaByLinha = [];
        this.existingCercasLinhaByLinha = [];
        this.unidadesLinhaIds = [];
        return;
      }
      this.applyAssistantLinhaSelection(linha);
    },
    applyAssistantLinhaSelection(linha) {
      if (!linha || linha.id_linha == null) return;
      this.editedItem.id_linha = linha.id_linha;
      this.editedItem.id_empresa = linha.id_empresa;
      this.editedItem.nome_linha = linha.nome_linha;
      this.editedItem.descricao_linha = linha.descricao_linha != null ? linha.descricao_linha : null;
      this.editedItem.cod_linha = linha.cod_linha;
      this.editedItem.id_rota = linha.id_rota;
      this.editedItem.distancia_ida = linha.distancia_ida;
      this.editedItem.distancia_volta = linha.distancia_volta;
      this.editedItem.lotacao_prevista = linha.lotacao_prevista;
      this.editedItem.linha_intermunicipal = linha.linha_intermunicipal;
      this.editedItem.has_eta = linha.has_eta;
      this.editedItem.id_tipo_aberturas_viagens = linha.id_tipo_aberturas_viagens;
      this.editedItem.id_linha_principal = linha.id_linha_principal || null;
      this.editedItem.cod_integracao_prodata_tz = linha.cod_integracao_prodata_tz != null ? linha.cod_integracao_prodata_tz : null;
      this.isAtiva = Number(linha.ativa) === 1;
      this.isCircular = Number(linha.circular) === 1;
      this.velocidadeMaxima = linha.velocidade_maxima;
      this.alarmeVelocidadeMaxima = Number(linha.alarme_velocidade_maxima) === 1;
      this.alarmeForaRota = Number(linha.alarme_fora_rota) === 1;
      this.toleranciaForaRota = linha.tolerancia_fora_rota;
      this.limiteTempoParado = linha.limite_tempo_parado;
      this.alarmeTempoParado = Number(linha.alarme_tempo_parado) === 1;
      this.limiteTempoLargada = linha.tempo_maximo_largada;
      this.atualizaInicioViagem = Number(linha.atualiza_inicio_viagem) === 1;
      if (linha.id_linha_principal) {
        this.isSublinha = true;
        this.linhaPrincipal = { id_linha: linha.id_linha_principal };
      } else {
        this.isSublinha = false;
        this.linhaPrincipal = null;
        this.resetLinhaSelectRef('linhaPrincipalSelect');
      }
      this.fetchAssistLineRotasECercas(linha.id_linha)
        .then(({ rotas, cercas }) => {
          this.existingRotasLinhaByLinha = rotas;
          this.existingCercasLinhaByLinha = cercas;
        })
        .catch((e) => {
          this.existingRotasLinhaByLinha = [];
          this.existingCercasLinhaByLinha = [];
          EventBus.$emit('show-error', { error: e });
        });
      this.populaUnidadesLinha(linha.id_linha);
    },
    /** Same composite key as cercas_linhas_uk01: id_cerca + id_linha + id_sentido + id_tipo_cerca (linha checked at persist). */
    isCercaAssociationAlreadyOnAssistantLine(id_cerca, id_tipo_cerca, id_sentido, cercasArr) {
      const ic = Number(id_cerca);
      const tt = Number(id_tipo_cerca);
      const sd = Number(id_sentido);
      if (!Number.isFinite(ic) || !Number.isFinite(tt) || !Number.isFinite(sd)) return false;
      const arr = Array.isArray(cercasArr) ? cercasArr : this.existingCercasLinhaByLinha || [];
      return arr.some(
        (c) =>
          Number(c.id_cerca) === ic && Number(c.id_tipo_cerca) === tt && Number(c.id_sentido) === sd,
      );
    },
    /** Match rota on the selected line: same cod_rota first, else id_sentido + id_tipo_rota. */
    findExistingRotaForKmlRow(row, rotasArr) {
      const arr = Array.isArray(rotasArr) ? rotasArr : this.existingRotasLinhaByLinha || [];
      const cod = String(row.cod_rota || '').trim();
      if (cod) {
        for (let i = 0; i < arr.length; i++) {
          const r = arr[i];
          if (String(r.cod_rota || '').trim() === cod) return r;
        }
      }
      const sent = Number(row.id_sentido);
      const tipo = Number(row.id_tipo_rota);
      if (!Number.isFinite(sent) || !Number.isFinite(tipo)) return null;
      for (let i = 0; i < arr.length; i++) {
        const r = arr[i];
        if (Number(r.id_sentido) === sent && Number(r.id_tipo_rota) === tipo) return r;
      }
      return null;
    },
    rotaCodDupNewCodRule(c) {
      const v = String(c || '').trim();
      if (!v) return this.$t('crudLinhas.validation.required');
      if (!/^[a-zA-Z0-9_ -]{1,50}$/.test(v)) {
        return this.$t('crudLinhas.kmlAssistant.rotaCodDuplicateInvalidCod');
      }
      return true;
    },
    rotaCodDupCloseDialog() {
      this.rotaCodDupDialogOpen = false;
      this.rotaCodDupExisting = null;
      this.rotaCodDupDuplicateCod = '';
      this.rotaCodDupNewCodInput = '';
      this.rotaCodDupStep = 'choose';
    },
    rotaCodDupGoToChangeCode() {
      this.rotaCodDupStep = 'newCode';
      this.rotaCodDupNewCodInput = '';
    },
    rotaCodDupBackToChoice() {
      this.rotaCodDupStep = 'choose';
    },
    openRotaCodDuplicateDialog(globalRota, duplicateCod) {
      this.rotaCodDupExisting = globalRota;
      this.rotaCodDupDuplicateCod = duplicateCod;
      this.rotaCodDupNewCodInput = '';
      this.rotaCodDupStep = 'choose';
      return new Promise((resolve, reject) => {
        this.rotaCodDupResolveFn = resolve;
        this.rotaCodDupRejectFn = reject;
        this.rotaCodDupDialogOpen = true;
      });
    },
    rotaCodDupConfirmUpdate() {
      const fn = this.rotaCodDupResolveFn;
      this.rotaCodDupResolveFn = null;
      this.rotaCodDupRejectFn = null;
      this.rotaCodDupCloseDialog();
      if (fn) fn({ action: 'update' });
    },
    rotaCodDupConfirmNew() {
      const c = String(this.rotaCodDupNewCodInput || '').trim();
      const rule = this.rotaCodDupNewCodRule(c);
      if (rule !== true) {
        EventBus.$emit('show-alert', { mensagem: rule, tipo: 'error' });
        return;
      }
      if (c === this.rotaCodDupDuplicateCod) {
        EventBus.$emit('show-alert', {
          mensagem: this.$t('crudLinhas.kmlAssistant.rotaCodDuplicateSameAsDuplicate'),
          tipo: 'error',
        });
        return;
      }
      const fn = this.rotaCodDupResolveFn;
      this.rotaCodDupResolveFn = null;
      this.rotaCodDupRejectFn = null;
      this.rotaCodDupCloseDialog();
      if (fn) fn({ action: 'insert', cod_rota: c });
    },
    rotaCodDupCancel() {
      const fn = this.rotaCodDupRejectFn;
      this.rotaCodDupResolveFn = null;
      this.rotaCodDupRejectFn = null;
      this.rotaCodDupCloseDialog();
      if (fn) {
        const e = new Error('ROTA_COD_DUP_CANCEL');
        e.code = 'ROTA_COD_DUP_CANCEL';
        fn(e);
      }
    },
    async fetchRotaByCodGlobal(cod, idCliente) {
      const trimmed = String(cod || '').trim();
      if (!trimmed) return null;
      try {
        const body = { cod_rota: trimmed };
        if (idCliente !== undefined) {
          body.id_cliente = idCliente === '' || idCliente == null ? null : idCliente;
        }
        const res = await apiController.request(apiController.endpoints.getRotaporcodigo, {
          data: body,
          silent: true,
        });
        const r = res.data;
        if (r && r.id_rota != null) return r;
        return null;
      } catch (e) {
        const status = e.response && e.response.status;
        if (status === 401 || status === 403) {
          EventBus.$emit('show-alert', {
            mensagem: this.$t('crudLinhas.kmlAssistant.rotaFetchByCodPermissionDenied'),
            tipo: 'error',
          });
        } else if (!e.response) {
          EventBus.$emit('show-alert', {
            mensagem: this.$t('crudLinhas.kmlAssistant.rotaFetchByCodNetworkError'),
            tipo: 'error',
          });
        } else {
          EventBus.$emit('show-error', { error: e });
        }
        throw e;
      }
    },
    /**
     * When updating by line match, cod_rota may belong to another id_rota (UK conflict on UPDATE).
     * Uses the same duplicate-code dialog as the insert path (update global owner vs change code).
     */
    async resolveUpdatelorotaIdWhenCodOwnedElsewhere(lineMatchedIdRota, batchCodKey, row, payload) {
      if (!batchCodKey) return lineMatchedIdRota;
      const global = await this.fetchRotaByCodGlobal(batchCodKey, payload && payload.id_cliente);
      if (!global || global.id_rota == null) return lineMatchedIdRota;
      if (Number(global.id_rota) === Number(lineMatchedIdRota)) return lineMatchedIdRota;
      const choice = await this.openRotaCodDuplicateDialog(global, batchCodKey);
      if (choice.action === 'update') return global.id_rota;
      if (choice.action === 'insert') {
        const c = String(choice.cod_rota || '').trim();
        if (row) this.$set(row, 'cod_rota', c);
        else if (payload) payload.cod_rota = c;
        return lineMatchedIdRota;
      }
      return lineMatchedIdRota;
    },
    /**
     * Pre-resolves id_rota vs global cod owner (dialog), PUT updaterota, and on 409 duplicate runs the dialog + retry
     * so conflicts still surface even if prefetch missed (permissions, race) or another UK applies.
     */
    async putUpdatelorotaWithCodeOwnerFlow(payload, row) {
      const cod = String(payload.cod_rota || '').trim();
      let idRota = await this.resolveUpdatelorotaIdWhenCodOwnedElsewhere(payload.id_rota, cod, row, payload);
      const buildBody = () => {
        const cod_rota = row
          ? String(row.cod_rota != null ? row.cod_rota : payload.cod_rota || '').trim()
          : String(payload.cod_rota || '').trim();
        const id_cliente =
          payload.id_cliente !== undefined && payload.id_cliente !== null && payload.id_cliente !== ''
            ? payload.id_cliente
            : null;
        return {
          id_rota: idRota,
          cod_rota,
          nome_rota: String(payload.nome_rota).trim(),
          id_empresa: payload.id_empresa,
          id_cliente,
          ativa: payload.ativa,
          shape: payload.shape,
          geojson: payload.geojson,
          distancia: payload.distancia != null ? payload.distancia : 0,
          observacoes: payload.observacoes != null ? payload.observacoes : '',
        };
      };
      try {
        await apiController.request(apiController.endpoints.updateRota, {
          data: buildBody(),
          silent: true,
        });
      } catch (e) {
        const msg0 = String(
          (e.response && e.response.data && (e.response.data.mensagem || e.response.data.msg)) || '',
        ).toLowerCase();
        const looksLikeDuplicate =
          msg0.includes('duplic') || msg0.includes('duplicate') || msg0.includes('único') || msg0.includes('unico');
        const codAfter = row
          ? String(row.cod_rota != null ? row.cod_rota : payload.cod_rota || '').trim()
          : cod;
        if (codAfter && e.response && e.response.status === 409 && looksLikeDuplicate) {
          const global = await this.fetchRotaByCodGlobal(codAfter, buildBody().id_cliente);
          if (global && global.id_rota != null && Number(global.id_rota) !== Number(idRota)) {
            const choice = await this.openRotaCodDuplicateDialog(global, codAfter);
            if (choice.action === 'update') {
              idRota = global.id_rota;
            } else if (choice.action === 'insert') {
              const c = String(choice.cod_rota || '').trim();
              if (row) this.$set(row, 'cod_rota', c);
              else payload.cod_rota = c;
            }
            await apiController.request(apiController.endpoints.updateRota, {
          data: buildBody(),
          silent: true,
        });
          } else {
            const m = e.response && e.response.data && (e.response.data.mensagem || e.response.data.msg);
            throw new Error(m || e.message || 'updaterota');
          }
        } else {
          const m = e.response && e.response.data && (e.response.data.mensagem || e.response.data.msg);
          throw new Error(m || e.message || 'updaterota');
        }
      }
      return idRota;
    },
    async persistKmlEntities(routeRows, fenceRows, persistOpts) {
      const routes = routeRows != null ? routeRows : this.kmlRouteRows;
      const fences = fenceRows != null ? fenceRows : this.kmlFenceRows;
      this.rotasLinha = [];
      this.cercasLinha = [];
      const po = persistOpts || {};
      const updateExisting =
        typeof po.updateExisting === 'boolean' ? po.updateExisting : this.assistantUpdatingExistingLinha;
      const existingRotasForPersist = Array.isArray(po.existingRotasLinhaByLinha)
        ? po.existingRotasLinhaByLinha
        : this.existingRotasLinhaByLinha || [];
      const existingCercasForPersist = Array.isArray(po.existingCercasLinhaByLinha)
        ? po.existingCercasLinhaByLinha
        : this.existingCercasLinhaByLinha || [];
      const idClientePreferred = Object.prototype.hasOwnProperty.call(po, 'idCliente')
        ? po.idCliente
        : this.editedItem.id_cliente;
      const idEmpresaPreferred = Object.prototype.hasOwnProperty.call(po, 'idEmpresa')
        ? po.idEmpresa
        : this.editedItem.id_empresa;
      const lineScope = this.resolveKmlPersistEmpresaCliente(null, idClientePreferred, idEmpresaPreferred);
      const idClienteCtx = lineScope.id_cliente;
      const rotaIdByCodInBatch = new Map();
      for (const row of routes) {
        const batchCodKey = String(row.cod_rota || '').trim();
        const shapeStr = lineStringFeatureToMerusShape(row.feature);
        if (!shapeStr) {
          throw new Error('invalid_line');
        }
        const geojsonStr = JSON.stringify(row.feature);
        const tipo = this.tiposRota.find((t) => t.id_tipo_rota === row.id_tipo_rota);
        const sent = this.sentidos.find((s) => s.id_sentido === row.id_sentido);
        const { id_empresa: idEmpresaRota, id_cliente: idClienteRota } = this.resolveKmlPersistEmpresaCliente(
          row.id_empresa,
          idClienteCtx,
          idEmpresaPreferred,
        );
        let id_rota;
        let needsNewLinhaRotaBulkLink = true;
        if (row.matchedExistingId) {
          id_rota = row.matchedExistingId;
          const onLine = (existingRotasForPersist || []).some(
            (r) => Number(r.id_rota) === Number(id_rota),
          );
          needsNewLinhaRotaBulkLink = updateExisting ? !onLine : true;
          if (batchCodKey) rotaIdByCodInBatch.set(batchCodKey, id_rota);
        } else {
        let existingHit = updateExisting ? this.findExistingRotaForKmlRow(row, existingRotasForPersist) : null;
        if (!existingHit && batchCodKey && rotaIdByCodInBatch.has(batchCodKey)) {
          existingHit = { id_rota: rotaIdByCodInBatch.get(batchCodKey) };
        }
        if (existingHit) {
          const lineMatchedIdRota = existingHit.id_rota;
          const updatePayload = {
            id_rota: lineMatchedIdRota,
            cod_rota: String(row.cod_rota).trim(),
            nome_rota: String(row.nome_rota).trim(),
            id_empresa: idEmpresaRota,
            id_cliente: idClienteRota,
            ativa: 1,
            shape: shapeStr,
            geojson: geojsonStr,
            distancia: 0,
            observacoes: '',
          };
          id_rota = await this.putUpdatelorotaWithCodeOwnerFlow(updatePayload, row);
          if (Number(id_rota) === Number(lineMatchedIdRota)) {
            needsNewLinhaRotaBulkLink = false;
          } else {
            const onLine = (existingRotasForPersist || []).some(
              (r) => Number(r.id_rota) === Number(id_rota),
            );
            needsNewLinhaRotaBulkLink = updateExisting ? !onLine : true;
          }
          {
            const codForBatchMap = String(row.cod_rota || '').trim();
            if (codForBatchMap) rotaIdByCodInBatch.set(codForBatchMap, id_rota);
          }
        } else {
          let useUpdateId = null;
          let codForInsert = batchCodKey;

          if (batchCodKey) {
            const globalRota = await this.fetchRotaByCodGlobal(batchCodKey, idClienteCtx);
            if (globalRota && globalRota.id_rota != null) {
              const choice = await this.openRotaCodDuplicateDialog(globalRota, batchCodKey);
              if (choice.action === 'update') {
                useUpdateId = globalRota.id_rota;
              } else {
                codForInsert = String(choice.cod_rota || '').trim();
                this.$set(row, 'cod_rota', codForInsert);
                const again = await this.fetchRotaByCodGlobal(codForInsert, idClienteCtx);
                if (again && again.id_rota != null) {
                  throw new Error(this.$t('crudLinhas.kmlAssistant.rotaCodDuplicateNewCodeAlsoExists'));
                }
              }
            }
          }

          if (useUpdateId != null) {
            const updatePayload = {
              id_rota: useUpdateId,
              cod_rota: batchCodKey,
              nome_rota: String(row.nome_rota).trim(),
              id_empresa: idEmpresaRota,
              id_cliente: idClienteRota,
              ativa: 1,
              shape: shapeStr,
              geojson: geojsonStr,
              distancia: 0,
              observacoes: '',
            };
            id_rota = await this.putUpdatelorotaWithCodeOwnerFlow(updatePayload, row);
            const onLine = (existingRotasForPersist || []).some(
              (r) => Number(r.id_rota) === Number(id_rota),
            );
            needsNewLinhaRotaBulkLink = updateExisting ? !onLine : true;
            {
              const codForBatchMap = String(row.cod_rota || '').trim();
              if (codForBatchMap) rotaIdByCodInBatch.set(codForBatchMap, id_rota);
            }
          } else {
            const payload = {
              cod_rota: codForInsert,
              nome_rota: String(row.nome_rota).trim(),
              id_empresa: idEmpresaRota,
              id_cliente: idClienteRota,
              ativa: 1,
              shape: shapeStr,
              geojson: geojsonStr,
              distancia: 0,
              observacoes: '',
            };
            let res;
            try {
              res = await apiController.request(apiController.endpoints.insertRota, {
                data: payload,
                silent: true,
              });
            } catch (e) {
              const m = e.response && e.response.data && (e.response.data.mensagem || e.response.data.msg);
              throw new Error(m || e.message || 'insertrota');
            }
            if (res.data.erro || res.data.id_rota == null) {
              throw new Error(res.data.mensagem || 'insertrota');
            }
            id_rota = res.data.id_rota;
            needsNewLinhaRotaBulkLink = true;
            {
              const codForBatchMap = String(row.cod_rota || '').trim();
              if (codForBatchMap) rotaIdByCodInBatch.set(codForBatchMap, id_rota);
            }
          }
        }
        }
        this.rotasLinha.push({
          id_rota,
          id_tipo_rota: row.id_tipo_rota,
          id_sentido: row.id_sentido,
          nome_rota: row.matchedExistingId ? this.kmlRouteNomeFieldDisplay(row) : row.nome_rota,
          descricao_tipo_rota: tipo ? tipo.descricao_tipo_rota : null,
          descricao_sentido: sent ? sent.descricao_sentido : null,
          needsNewLinhaRotaBulkLink,
        });
      }
      for (const row of fences) {
        let id_cerca;
        if (row.matchedExistingId) {
          id_cerca = row.matchedExistingId;
        } else {
          const codCercaApi = sanitizeCercaCodForInsert(row.cod_cerca);
          if (!codCercaApi) {
            throw new Error(this.$t('crudLinhas.kmlAssistant.invalidFenceCode'));
          }
          let payload;
          if (row.kind === 'point') {
            const [lng, lat] = row.feature.geometry.coordinates;
            payload = {
              cod_cerca: codCercaApi,
              nome_cerca: String(row.nome_cerca).trim(),
              alarme_entrada: 0,
              alarme_saida: 0,
              endereco: '',
              shape: '[]',
              lat_centro: Number(lat),
              lng_centro: Number(lng),
              alarme_velocidade: 0,
              velocidade_maxima: null,
              geojson: null,
              is_circular: 1,
              raio_mts: Number(row.raio_mts) > 0 ? Number(row.raio_mts) : 75,
              observacoes: '',
              ativa: 1,
              id_empresa: lineScope.id_empresa,
              id_cliente: lineScope.id_cliente,
            };
          } else {
            const shapeStr = polygonFeatureToMerusShape(row.feature);
            if (!shapeStr) {
              throw new Error('invalid_polygon');
            }
            const ring =
              row.feature &&
              row.feature.geometry &&
              Array.isArray(row.feature.geometry.coordinates) &&
              row.feature.geometry.coordinates[0];
            const centroid = ringCentroidLngLat(ring);
            if (!centroid || !Number.isFinite(centroid.lat) || !Number.isFinite(centroid.lng)) {
              throw new Error('invalid_polygon');
            }
            payload = {
              cod_cerca: codCercaApi,
              nome_cerca: String(row.nome_cerca).trim(),
              alarme_entrada: 0,
              alarme_saida: 0,
              endereco: '',
              shape: shapeStr,
              // lat/lng centro são NOT NULL na tabela; polígono usa o centróide do anel.
              lat_centro: Number(Number(centroid.lat).toFixed(6)),
              lng_centro: Number(Number(centroid.lng).toFixed(6)),
              alarme_velocidade: 0,
              velocidade_maxima: null,
              geojson: JSON.stringify(row.feature),
              is_circular: 0,
              // NOT NULL na tabela; polígono usa 0 (igual CRUDCercas).
              raio_mts: 0,
              observacoes: '',
              ativa: 1,
              id_empresa: lineScope.id_empresa,
              id_cliente: lineScope.id_cliente,
            };
          }
          let res;
          try {
            res = await apiController.request(apiController.endpoints.insertCerca, {
              data: payload,
              silent: true,
            });
          } catch (e) {
            const m = e.response && e.response.data && (e.response.data.mensagem || e.response.data.msg);
            throw new Error(m || e.message || 'insertcerca');
          }
          if (res.data.erro || res.data.id_cerca == null) {
            throw new Error(res.data.mensagem || 'insertcerca');
          }
          id_cerca = res.data.id_cerca;
        }
        const tipoCerca = this.tiposCerca.find((t) => t.id_tipo_cerca === row.id_tipo_cerca);
        const sent = this.sentidos.find((s) => s.id_sentido === row.id_sentido);
        const showSin = row.id_tipo_cerca === 2 || row.id_tipo_cerca === 3 ? 1 : 0;
        const cercaAlreadyOnLine =
          updateExisting &&
          this.isCercaAssociationAlreadyOnAssistantLine(
            id_cerca,
            row.id_tipo_cerca,
            row.id_sentido,
            existingCercasForPersist,
          );
        this.cercasLinha.push({
          id_cerca,
          id_tipo_cerca: row.id_tipo_cerca,
          nome_cerca: row.matchedExistingId ? this.kmlFenceNomeFieldDisplay(row) : row.nome_cerca,
          descricao_tipo_cerca: tipoCerca ? tipoCerca.descricao_tipo_cerca : null,
          cod_tipo_cerca: tipoCerca ? tipoCerca.codigo_cerca : null,
          id_sentido: row.id_sentido,
          descricao_sentido: sent ? sent.descricao_sentido : null,
          ordem_parada: row.ordem_parada,
          show_sinotico: showSin,
          tempo_chegada_minutos: (() => {
            const t = row.tempo_chegada_minutos;
            if (t == null || t === '') return null;
            const n = Number(t);
            return Number.isFinite(n) ? n : null;
          })(),
          needsNewLinhaCercaBulkLink: !cercaAlreadyOnLine,
        });
      }
      this.editedRotas = this.rotasLinha.length > 0;
      this.editedCercas = this.cercasLinha.length > 0;
    },
    async runSaveMultipleKmlLines() {
      this.operacao = 0;
      this.savingLine = true;
      const groups = [...this.kmlActiveGroupsOrdered];
      const turnosCopy =
        this.editedTurnosPassageiros && this.turnosPassageirosLinha.length
          ? JSON.parse(JSON.stringify(this.turnosPassageirosLinha))
          : null;
      try {
        let anyGroupUpdated = false;
        for (let i = 0; i < groups.length; i++) {
          const gid = groups[i];
          const routes = this.kmlRouteRows.filter((r) => this.rowBelongsToKmlGroup(r, gid));
          const fences = this.kmlFenceRows.filter((f) => this.rowBelongsToKmlGroup(f, gid));
          const draft = this.kmlMultiLineDrafts[gid];
          if (!draft || !routes.length) {
            throw new Error(this.$t('crudLinhas.kmlAssistant.multiDraftValidation'));
          }
          const updating = !!(draft.editedItem && draft.editedItem.id_linha);
          if (updating) anyGroupUpdated = true;
          this.persistAssistantUpdatingExistingOverride = updating;
          try {
            await this.persistKmlEntities(routes, fences, {
              updateExisting: updating,
              existingRotasLinhaByLinha: draft.existingRotasLinhaByLinha || [],
              existingCercasLinhaByLinha: draft.existingCercasLinhaByLinha || [],
              idCliente: draft.editedItem ? draft.editedItem.id_cliente : null,
              idEmpresa: draft.editedItem ? draft.editedItem.id_empresa : null,
            });
            const payload = this.buildInsertLinhaPayloadFromDraft(draft);
            this.syncIdRotaFromRotasLinhaInto(payload);
            if (!payload.id_rota) {
              throw new Error(this.$t('crudLinhas.wizard.missingPrimaryRoute'));
            }
            let idlinha;
            if (updating) {
              const res = await apiController.request(apiController.endpoints.updateLinha, {
                data: payload,
                silent: true,
              });
              if (res.data.erro) {
                throw new Error(res.data.mensagem || 'updatelinha');
              }
              idlinha = draft.editedItem.id_linha;
            } else {
              const res = await apiController.request(apiController.endpoints.insertLinha, {
                data: payload,
                silent: true,
              });
              if (res.data.erro) {
                throw new Error(res.data.mensagem || 'insertlinha');
              }
              idlinha = res.data.insertId;
            }
            const promises = [];
            if (this.editedCercas) promises.push(this.insertCercasBulkAsync(idlinha));
            if (this.editedRotas) promises.push(this.insertRotasBulkAsync(idlinha));
            if (promises.length) {
              await Promise.all(promises);
            }
            if (this.editedTurnosPassageiros && turnosCopy && turnosCopy.length) {
              await this.insertTurnosPassageirosBulkAsync(idlinha, turnosCopy);
            }
            this.scheduleCompararDistanciaInformada(idlinha);
          } finally {
            this.persistAssistantUpdatingExistingOverride = null;
          }
        }
        EventBus.$emit('show-alert', {
          mensagem: anyGroupUpdated
            ? this.$t('crudLinhas.messages.successUpdate')
            : this.$t('crudLinhas.messages.successInsert'),
          tipo: 'success',
        });
        this.$emit('saved');
        this.$emit('input', false);
      } catch (err) {
        const msg =
          err.code === 'ROTA_COD_DUP_CANCEL'
            ? this.$t('crudLinhas.kmlAssistant.rotaCodDuplicateCancelled')
            : err.code === 'ROTA_UPDATE_CODE_OWNER_CANCEL'
              ? this.$t('crudLinhas.kmlAssistant.rotaUpdateCodeOwnerCancelled')
              : err.message === 'invalid_line'
              ? this.$t('routs.alerts.error.incorrectGeometry')
              : err.message === 'invalid_polygon'
                ? this.$t('crudLinhas.messages.errorInsert')
                : err.message || this.$t('crudLinhas.messages.errorInsert');
        EventBus.$emit('show-alert', { mensagem: msg, tipo: 'error' });
      } finally {
        this.savingLine = false;
      }
    },
    save() {
      if (this.kmlMultiLineMode) {
        const draftErr = this.getMultiLineDraftValidationError();
        if (draftErr) {
          this.wizardStep = 1;
          EventBus.$emit('show-alert', { mensagem: draftErr, tipo: 'error' });
          return;
        }
      } else {
        if (!this.$refs.form) return;
        this.$refs.form.validate();
        if (!this.formValidado) {
          this.wizardStep = 1;
          return;
        }
      }
      if (!this.kmlRouteRows.length) {
        EventBus.$emit('show-alert', { mensagem: this.$t('crudLinhas.wizard.missingPrimaryRoute'), tipo: 'error' });
        this.wizardStep = 0;
        return;
      }
      if (this.kmlMultiLineMode) {
        const multiErr = this.getKmlMultiLineValidationError();
        if (multiErr) {
          this.wizardStep = 0;
          EventBus.$emit('show-alert', { mensagem: multiErr, tipo: 'error' });
          return;
        }
        this.runSaveMultipleKmlLines();
        return;
      }
      if (!this.kmlRouteRows.some((r) => this.isKmlPrimaryRota(r))) {
        EventBus.$emit('show-alert', { mensagem: this.$t('crudLinhas.wizard.missingPrimaryRoute'), tipo: 'error' });
        this.wizardStep = 0;
        return;
      }
      this.editedItem.ativa = this.isAtiva ? 1 : 0;
      this.editedItem.circular = this.isCircular ? 1 : 0;
      this.editedItem.tolerancia_fora_rota =
        this.toleranciaForaRota != null && this.toleranciaForaRota !== ''
          ? Number(this.toleranciaForaRota)
          : 150;
      this.editedItem.alarme_fora_rota = this.alarmeForaRota ? 1 : 0;
      this.editedItem.alarme_velocidade_maxima = this.alarmeVelocidadeMaxima ? 1 : 0;
      this.editedItem.velocidade_maxima = Number(this.velocidadeMaxima);
      this.editedItem.alarme_tempo_parado = this.alarmeTempoParado ? 1 : 0;
      this.editedItem.atualiza_inicio_viagem = this.atualizaInicioViagem ? 1 : 0;
      this.editedItem.limite_tempo_parado =
        this.limiteTempoParado != null && this.limiteTempoParado !== ''
          ? Number(this.limiteTempoParado)
          : 0;
      this.editedItem.tempo_maximo_largada =
        this.limiteTempoLargada != null && this.limiteTempoLargada !== ''
          ? Number(this.limiteTempoLargada)
          : 15;
      this.editedItem.distancia_ida =
        this.editedItem.distancia_ida != null && this.editedItem.distancia_ida !== ''
          ? Number(this.editedItem.distancia_ida)
          : 0;
      this.editedItem.distancia_volta =
        this.editedItem.distancia_volta != null && this.editedItem.distancia_volta !== ''
          ? Number(this.editedItem.distancia_volta)
          : 0;
      this.editedItem.id_tipo_aberturas_viagens = this.editedItem.id_tipo_aberturas_viagens || 1;
      this.editedItem.has_eta = this.editedItem.has_eta ? 1 : 0;
      this.editedItem.linha_intermunicipal = this.editedItem.linha_intermunicipal ? 1 : 0;
      this.editedItem.lotacao_prevista =
        this.editedItem.lotacao_prevista != null && this.editedItem.lotacao_prevista !== ''
          ? Number(this.editedItem.lotacao_prevista)
          : 0;
      this.operacao = 0;
      this.savingLine = true;
      (async () => {
        try {
          await this.persistKmlEntities(this.kmlRouteRows, this.kmlFenceRows, {
            idCliente: this.editedItem.id_cliente,
            idEmpresa: this.editedItem.id_empresa,
          });
        } catch (err) {
          this.savingLine = false;
          EventBus.$emit('show-alert', {
            mensagem:
              err.code === 'ROTA_COD_DUP_CANCEL'
                ? this.$t('crudLinhas.kmlAssistant.rotaCodDuplicateCancelled')
                : err.code === 'ROTA_UPDATE_CODE_OWNER_CANCEL'
                  ? this.$t('crudLinhas.kmlAssistant.rotaUpdateCodeOwnerCancelled')
                  : err.message === 'invalid_line'
                  ? this.$t('routs.alerts.error.incorrectGeometry')
                  : err.message || this.$t('crudLinhas.messages.errorInsert'),
            tipo: 'error',
          });
          return;
        }
        this.syncIdRotaFromRotasLinha();
        if (!this.editedItem.id_rota) {
          this.savingLine = false;
          EventBus.$emit('show-alert', { mensagem: this.$t('crudLinhas.wizard.missingPrimaryRoute'), tipo: 'error' });
          this.wizardStep = 0;
          return;
        }
        const isUpdate = this.assistantUpdatingExistingLinha;
        const idLinhaExisting = this.editedItem.id_linha;
        const afterLinhaSave = (response, idlinha, erroFlag) => {
          if (erroFlag) {
            this.savingLine = false;
            EventBus.$emit('show-alert', {
              mensagem: this.$t(isUpdate ? 'crudLinhas.messages.errorUpdate' : 'crudLinhas.messages.errorInsert'),
              tipo: 'error',
            });
            return;
          }
          if (idlinha != null && this.editedItem) {
            this.editedItem.id_linha = idlinha;
          }
          this._pendingCompararIdLinha = idlinha;
          const pendingCercas = this.editedCercas;
          const pendingRotasBulk = this.editedRotas && this.rotasLinhaNeedsBulkInsert();
          const pendingTurnos = this.editedTurnosPassageiros;
          if (pendingCercas) {
            this.insertCercasBulk(idlinha);
          }
          if (pendingRotasBulk) {
            this.insertRotasBulk(idlinha);
          }
          if (pendingTurnos) {
            const copy = JSON.parse(JSON.stringify(this.turnosPassageirosLinha));
            this.insertTurnosPassageirosBulk(idlinha, copy);
          }
          if (!pendingCercas && !pendingRotasBulk && !pendingTurnos) {
            this.updateTable(false);
          }
        };
        if (isUpdate) {
          apiController.request(apiController.endpoints.updateLinha, {
            data: this.editedItem,
            silent: true,
            onSuccess: (response) => {
              afterLinhaSave(response, idLinhaExisting, response.data.erro);
            },
            onError: (e) => {
              this.savingLine = false;
              const data = e && e.response && e.response.data;
              const apiMsg = data && (data.mensagem || data.message || data.msg);
              EventBus.$emit('show-alert', {
                mensagem: apiMsg || this.$t('crudLinhas.messages.errorUpdate'),
                tipo: 'error',
              });
            },
          });
        } else {
          apiController.request(apiController.endpoints.insertLinha, {
            data: this.editedItem,
            silent: true,
            onSuccess: (response) => {
              if (response.data.erro) {
                console.error('Erro ao inserir linha:', response.data.mensagem);
                this.savingLine = false;
                EventBus.$emit('show-alert', {
                  mensagem: response.data.mensagem || this.$t('crudLinhas.messages.errorInsert'),
                  tipo: 'error',
                });
                return;
              }
              afterLinhaSave(response, response.data.insertId, response.data.erro);
            },
            onError: (e) => {
              this.savingLine = false;
              const data = e && e.response && e.response.data;
              const apiMsg = data && (data.mensagem || data.message || data.msg);
              EventBus.$emit('show-alert', {
                mensagem: apiMsg || this.$t('crudLinhas.messages.errorInsert'),
                tipo: 'error',
              });
            },
          });
        }
      })();
    },
    updateTable(status) {
      if (this._isDestroyed) return;
      if (
        !this.loadingInsertCercas &&
        !this.loadingInsertRotas &&
        !this.loadingInsertSublinhas &&
        !this.loadingInsertTurnosPassageiros
      ) {
        if (status) {
          EventBus.$emit('show-alert', { mensagem: this.$t('crudLinhas.messages.errorInsert'), tipo: 'error' });
        } else {
          const idComparar =
            this._pendingCompararIdLinha ||
            (this.editedItem && this.editedItem.id_linha) ||
            null;
          this._pendingCompararIdLinha = null;
          if (idComparar) {
            this.scheduleCompararDistanciaInformada(idComparar);
          }
          EventBus.$emit('show-alert', {
            mensagem: this.assistantUpdatingExistingLinha
              ? this.$t('crudLinhas.messages.successUpdate')
              : this.$t('crudLinhas.messages.successInsert'),
            tipo: 'success',
          });
          this.$emit('saved');
          this.$emit('input', false);
        }
        this.savingLine = false;
      }
    },
    saveTurnosPassageiros() {
      if (!this.$refs.formTurnosPassageiros) return;
      this.$refs.formTurnosPassageiros.validate();
      if (!this.formValidadoTurnosPassageiros) return;
      if (this.itemTurnosPassageiros.id_turno == null) {
        EventBus.$emit('show-alert', { mensagem: this.$t('crudLinhas.validation.required'), tipo: 'error' });
        return;
      }
      if (this.editingTurnosPassageiros) {
        const idx = this.turnosPassageirosLinha.findIndex((a) => a.id_turno === this.itemTurnosPassageiros.id_turno);
        if (idx !== -1) {
          this.turnosPassageirosLinha.splice(idx, 1);
          this.turnosPassageirosLinha.push({ ...this.itemTurnosPassageiros });
        }
      } else {
        const turno = this.turnosPassageiros.find((t) => t.id_turno === this.itemTurnosPassageiros.id_turno);
        this.turnosPassageirosLinha.push({
          id_turno: this.itemTurnosPassageiros.id_turno,
          lotacao: this.itemTurnosPassageiros.lotacao,
          cod_turno: turno ? turno.cod_turno : null,
          descricao_turno: turno ? turno.descricao_turno : null,
        });
      }
      this.itemTurnosPassageiros = { ...this.defaultItemTurnosPassageiros };
      this.editingTurnosPassageiros = false;
      this.editedTurnosPassageiros = true;
    },
    editItemTurnosPassageiros(item) {
      this.itemTurnosPassageiros = { ...item };
      this.editingTurnosPassageiros = true;
    },
    deleteItemTurnosPassageiros(item) {
      this.turnosPassageirosLinha = this.turnosPassageirosLinha.filter((t) => t.id_turno !== item.id_turno);
      this.editedTurnosPassageiros = true;
    },
  },
};
</script>

<style scoped>
/* Block layout: avoid v-list-item flex so rows don’t collapse into one line until reflow */
.kml-routes-list {
  display: block;
  width: 100%;
}
.kml-routes-list > div {
  display: block;
  width: 100%;
  min-width: 0;
}
.kml-route-entry {
  display: block;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}
.kml-fences-list {
  display: block;
  width: 100%;
}
.kml-fences-list > div {
  display: block;
  width: 100%;
  min-width: 0;
}
.kml-fence-entry {
  display: block;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}
.kml-fence-entry--selected {
  background-color: rgba(25, 118, 210, 0.06);
}
.linha-assistente-wizard-card {
  height: 100%;
  max-height: 100vh;
  min-height: 0;
  overflow: hidden;
}
/* Map sidebar layout uses flex on the card; keep toolbar only as tall as its rows (no flex growth). */
.linha-assistente-wizard-toolbar {
  flex: 0 0 auto !important;
  align-self: stretch;
}
/* flex-basis 0 so this row takes only the space below the toolbar, not content height (enables inner scroll). */
.linha-assistente-wizard-main-row {
  flex: 1 1 0% !important;
  min-height: 0 !important;
  overflow: hidden;
  max-height: 100%;
}
@media (min-width: 960px) {
  .linha-assistente-wizard-main-row {
    flex-wrap: nowrap !important;
  }
}
.linha-assistente-wizard-col-content {
  min-height: 0;
  max-height: 100%;
  overflow: hidden;
}
.linha-assistente-wizard-scroll {
  min-height: 0 !important;
  overflow-y: auto !important;
  -webkit-overflow-scrolling: touch;
}
.linha-assistente-wizard-col-map {
  min-height: 0;
  max-height: 100%;
  overflow: hidden;
  border-left: 1px solid rgba(0, 0, 0, 0.12);
  background: #fff;
}
@media (max-width: 959px) {
  .linha-assistente-wizard-main-row {
    flex-direction: column !important;
    flex-wrap: nowrap !important;
  }
  .linha-assistente-wizard-col-content {
    flex: 1 1 0% !important;
    min-height: 0 !important;
    max-height: none;
  }
  .linha-assistente-wizard-col-map {
    flex: 0 0 auto !important;
    border-left: none;
    border-top: 1px solid rgba(0, 0, 0, 0.12);
    min-height: 42vh;
    max-height: 50vh;
  }
}
</style>
